import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Breadcrumbs,
  Link,
  Paper,
  Avatar,
  Chip,
  useMediaQuery,
  AppBar,
  Toolbar,
  CircularProgress,
  Backdrop,
  Container,
  Fade,
  Slide,
  Stack,
  Badge,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  EmojiEvents as TrophyIcon,
  Groups as TeamsIcon,
  Person as PersonIcon,
  NavigateNext as NavigateNextIcon,
  School as SchoolIcon,
  SportsBasketball as BasketballIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  VideoLibrary as VideoLibraryIcon,
  Description as DescriptionIcon,
  Healing as HealingIcon,
  Gavel as GavelIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Language as LanguageIcon,
  CompareArrows as CompareArrowsIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import CompetitionPerformance from './CompetitionPerformance';
import api from '../../api/axiosConfig';
import { getMediaBaseUrl } from '../../utils/mediaUrl';
import { usePositions } from '../../utils/positions';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  VIRTUAL_NIVEAU_ID,
  VIRTUAL_NIVEAU_FA_ID,
  buildVirtualCompetitionId,
  buildVirtualEquipeId,
  isVirtualId,
  isFreeAgentVirtualId,
  parseVirtualId,
  getCountryFlagUrl,
  getCountryLabel,
  getAgeCategoryLabel,
} from './nationalSelectionsConfig';

// Composant de chargement
const LoadingBackdrop = ({ open }) => (
  <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.drawer + 9999 }}>
    <CircularProgress color="inherit" />
  </Backdrop>
);

// Style pour les badges comme dans PlayerResults.js
const badgeStyle = (bgColor) => ({
  position: 'relative',
  width: 26,
  height: 26,
  borderRadius: '50%',
  backgroundColor: bgColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '15px',
  marginLeft: '-4px',
  border: '1px solid #fff',
});

// Fonction pour obtenir l'icône de forme
const getFormIcon = (forme) => {
  if (!forme) return null;
  switch (forme.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 16 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 16 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 16 }} />;
    default:
      return null;
  }
};

// Composant pour afficher les badges des joueurs (8 badges)
const PlayerBadges = ({ player, theme, t }) => {
  if (!player) return null;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {player.in_big_board === 1 && (
        <Tooltip title={t('competitionsViewer.badges.rankings')}>
          <Box sx={badgeStyle(theme.palette.primary.main)}>
            <StarIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_follow_list === 1 && (
        <Tooltip title={t('competitionsViewer.badges.tracking')}>
          <Box sx={badgeStyle(theme.palette.secondary.main)}>
            <VisibilityIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_informe === 1 && (
        <Tooltip title={t('competitionsViewer.badges.report')}>
          <Box sx={badgeStyle(theme.palette.success.main)}>
            <AssignmentIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_intel === 1 && (
        <Tooltip title={t('competitionsViewer.badges.intel')}>
          <Box sx={badgeStyle(theme.palette.warning.main)}>
            <LightbulbIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_fiche_technique === 1 && (
        <Tooltip title={t('competitionsViewer.badges.technicalSheet')}>
          <Box sx={badgeStyle(theme.palette.info.main)}>
            <DescriptionIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_video === 1 && (
        <Tooltip title={t('competitionsViewer.badges.video')}>
          <Box sx={badgeStyle("#9c27b0")}>
            <VideoLibraryIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.has_medidas === 1 && (
        <Tooltip title={t('competitionsViewer.badges.measurements')}>
          <Box sx={badgeStyle("#00bcd4")}>
            <StraightenIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.has_documents === 1 && (
        <Tooltip title={t('competitionsViewer.badges.documents')}>
          <Box sx={badgeStyle("#795548")}>
            <FolderOpenIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_injury === 1 && (
        <Tooltip title={t('competitionsViewer.badges.injury')}>
          <Box sx={badgeStyle(theme.palette.error.main)}>
            <HealingIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
      {player.in_tanteo === 1 && (
        <Tooltip title="Derecho de tanteo">
          <Box sx={badgeStyle('#f9a825')}>
            <GavelIcon fontSize="inherit" sx={{ fontSize: '15px' }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// Composant pour afficher les compteurs d'informe et scout
const CountBadge = ({ count, label, color }) => {
  const backgroundColor = count === 0 ? '#e0e0e0' : 
                          count === 1 ? '#ff9800' : 
                          '#4caf50';
  const textColor = count === 0 ? '#666' : '#ffffff';
  
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: backgroundColor,
        color: textColor,
        width: '22px',
        height: '22px',
        fontSize: '11px',
        fontWeight: 'bold',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        border: '1px solid #fff',
      }}
    >
      {count}
    </Box>
  );
};

// Mapping des positions pour regrouper les variations
const positionMappings = {
  'Base': ['Base', 'Point Guard', 'PG', 'Meneur'],
  'Escolta': ['Escolta', 'Shooting Guard', 'Guard', 'SG', 'Arrière'],
  'Alero': ['Alero', 'Small Forward', 'Swingman', 'SF', 'Ailier'],
  'Ala-Pívot': ['Ala-Pívot', 'Ala-Pivot', 'Power Forward', 'Power Forward / Center', 'PF', 'Ailier fort'],
  'Pívot': ['Pívot', 'Pivot', 'Center', 'C']
};

// Ordre des positions pour l'affichage
const positionOrder = ['Base', 'Escolta', 'Alero', 'Ala-Pívot', 'Pívot'];

// Ordre d'affichage des licences/passeports (autres valeurs triées alphabétiquement après)
const LICENCIA_ORDER = ['EXT', 'EUR', 'COT', 'JFL'];
const compareLicencias = (a, b) => {
  const ia = LICENCIA_ORDER.indexOf(a);
  const ib = LICENCIA_ORDER.indexOf(b);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return a.localeCompare(b);
};

// Couleurs pour chaque position
const positionColors = {
  'Base': '#2196F3',        // Bleu
  'Escolta': '#4CAF50',     // Vert
  'Alero': '#FF9800',       // Orange
  'Ala-Pívot': '#9C27B0',   // Violet
  'Pívot': '#F44336',       // Rouge
  'NO_POSITION': '#757575'  // Gris
};

// Clé spéciale pour les joueurs sans position
const NO_POSITION_KEY = 'NO_POSITION';

// Fonction pour normaliser une position
const normalizePosition = (position) => {
  if (!position) return NO_POSITION_KEY;

  for (const [normalizedPos, variations] of Object.entries(positionMappings)) {
    if (variations.some(v => v.toLowerCase() === position.toLowerCase())) {
      return normalizedPos;
    }
  }

  return position; // Si pas trouvé dans le mapping, retourner tel quel
};

// Composant principal
function CompetitionsViewer() {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const mediaUrl = getMediaBaseUrl();

  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:900px)');
  const location = useLocation();
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // États pour la navigation
  const [currentView, setCurrentView] = useState('niveaux'); // 'niveaux', 'competitions', 'equipes', 'joueurs'
  const [selectedNiveau, setSelectedNiveau] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedEquipe, setSelectedEquipe] = useState(null);
  
  // États pour les données
  const [niveaux, setNiveaux] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [joueurs, setJoueurs] = useState([]);
  const [cedidosSortants, setCedidosSortants] = useState([]);
  const [showCedidos, setShowCedidos] = useState(false);
  const [ncaaCommitsEntrants, setNcaaCommitsEntrants] = useState([]);

  // États pour toutes les données (pour la recherche globale)
  const [allCompetitions, setAllCompetitions] = useState([]);
  const [allEquipes, setAllEquipes] = useState([]);
  const [allJoueurs, setAllJoueurs] = useState([]);

  // Hiérarchie virtuelle des sélections nationales (chargée une fois)
  // { countries: [{ countryKey, flagCode, total, teams: [{ ageCategory, nb }] }] }
  const [nationalSelectionsData, setNationalSelectionsData] = useState(null);

  // Dialog "joueurs par passeport" (clic sur les chips de répartition)
  const [passportDialogOpen, setPassportDialogOpen] = useState(false);
  const [passportDialogTitle, setPassportDialogTitle] = useState('');
  const [passportDialogPlayers, setPassportDialogPlayers] = useState([]);
  const [passportDialogLoading, setPassportDialogLoading] = useState(false);
  
  // État pour le filtre global
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // États UI
  const [loading, setLoading] = useState(false);
  const [slideDirection, setSlideDirection] = useState('left');
  const [initialNavigationDone, setInitialNavigationDone] = useState(false);

  // États pour l'import de joueurs depuis la page équipe
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFormData, setImportFormData] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importLogs, setImportLogs] = useState([]);
  const [importPlayersData, setImportPlayersData] = useState([]);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importEurobasketDialog, setImportEurobasketDialog] = useState(false);
  const [importEurobasketOptions, setImportEurobasketOptions] = useState([]);
  const [importPendingPlayerData, setImportPendingPlayerData] = useState(null);
  const [importProballersPlayersData, setImportProballersPlayersData] = useState([]);
  const [importCurrentPlayerName, setImportCurrentPlayerName] = useState('');
  const [importSelectedOptionIndex, setImportSelectedOptionIndex] = useState(null);
  const [importEurobasketQueue, setImportEurobasketQueue] = useState([]);
  const importLogsEndRef = useRef(null);
  const [importTab, setImportTab] = useState(0); // 0 = par nom, 1 = par équipe

  // États pour l'import par équipe Eurobasket
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [teamSearchLoading, setTeamSearchLoading] = useState(false);
  const [teamSearchResults, setTeamSearchResults] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const [rosterTeamName, setRosterTeamName] = useState('');
  const [selectedRosterPlayers, setSelectedRosterPlayers] = useState(new Set());
  const [rosterImportLoading, setRosterImportLoading] = useState(false);
  const [rosterImportLogs, setRosterImportLogs] = useState([]);
  const [rosterImportResults, setRosterImportResults] = useState([]);
  const [rosterImportSuccess, setRosterImportSuccess] = useState(false);
  const [rosterImportError, setRosterImportError] = useState(null);
  const rosterLogsEndRef = useRef(null);
  
  // Historique de navigation
  const [navigationHistory, setNavigationHistory] = useState([]);
  
  // Chargement initial
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const equipeId = urlParams.get('equipe');

    // Toujours charger l'agrégat des sélections nationales en arrière-plan.
    loadNationalSelectionsAggregate();

    if (equipeId) {
      // Navigation directe depuis profil joueur: utiliser l'endpoint rapide
      // sauf si c'est un ID virtuel (sélection nationale) — dans ce cas on
      // attend l'agrégat puis on navigue via l'effet dédié.
      if (isVirtualId(equipeId)) {
        loadNiveaux();
        loadAllData();
      } else {
        fastNavigateToEquipe(parseInt(equipeId));
      }
    } else {
      // Navigation normale: charger tout
      loadNiveaux();
      loadAllData();
    }
  }, []);

  // Charger l'agrégat des sélections nationales (countries + teams + counts)
  const loadNationalSelectionsAggregate = async () => {
    try {
      const res = await api.get('/api/comp/national-selections/aggregate');
      setNationalSelectionsData(res.data || { countries: [] });
    } catch (error) {
      console.error('Error loading national selections aggregate:', error);
      setNationalSelectionsData({ countries: [] });
    }
  };

  // Charger toutes les données pour la recherche (lazy: différé si navigation directe)
  const loadAllData = async () => {
    try {
      // Charger toutes les compétitions
      const compRes = await api.get('/api/comp/competitions');
      console.log('Competitions loaded:', compRes.data);
      setAllCompetitions(compRes.data || []);

      // Charger toutes les équipes de toutes les compétitions
      const allEquipesData = [];
      const processedPairs = new Set(); // Pour éviter les doublons équipe-compétition

      for (const comp of compRes.data) {
        try {
          const equipesRes = await api.get(`/api/comp/competitions/${comp.id}/equipes`);
          equipesRes.data.forEach(equipe => {
            // Créer une clé unique pour la paire équipe-compétition
            const pairKey = `${equipe.id}-${comp.id}`;

            // Vérifier si cette paire n'a pas déjà été ajoutée
            if (!processedPairs.has(pairKey)) {
              processedPairs.add(pairKey);
              allEquipesData.push({
                ...equipe,
                competition_id: comp.id,
                competition_nom: comp.nom,
                niveau_id: comp.niveau_id,
                niveau_nom: comp.niveau_nom
              });
            }
          });
        } catch (error) {
          console.error(`Error loading equipes for competition ${comp.id}:`, error);
        }
      }

      setAllEquipes(allEquipesData);

      // Charger tous les joueurs
      try {
        const joueursRes = await api.get('/api/comp/joueurs/all');
        console.log('Joueurs loaded:', joueursRes.data);
        setAllJoueurs(joueursRes.data || []);
      } catch (error) {
        console.error('Error loading all joueurs:', error);
        setAllJoueurs([]);
      }
    } catch (error) {
      console.error('Error loading all data:', error);
    }
  };

  // Navigation rapide via le nouvel endpoint (1 appel context + 1 appel joueurs)
  const fastNavigateToEquipe = async (equipeId) => {
    // Sécurité: les IDs virtuels ne passent pas par cet endpoint
    if (isVirtualId(equipeId)) {
      loadNiveaux();
      loadAllData();
      return;
    }
    try {
      setLoading(true);

      // Appels en parallèle: contexte navigation + joueurs + compétitions avec stats.
      // L'endpoint /navigate renvoie une compétition ALLÉGÉE (sans passport_distribution
      // / nb_tanteo / nb_cedidos / nb_ncaa_commits) ; on l'enrichit via l'endpoint
      // d'en-tête, sinon les chips de stats disparaissent après un "Volver".
      const [navRes, joueursRes, compsStatsRes] = await Promise.all([
        api.get(`/api/comp/navigate/${equipeId}`),
        api.get(`/api/comp/equipes/${equipeId}/joueurs`),
        api.get('/api/comp/competitions')
      ]);

      const { niveau, competition, equipe, equipes: equipesData, competitions: competitionsData } = navRes.data;

      const compsWithStats = Array.isArray(compsStatsRes.data) ? compsStatsRes.data : [];
      const competitionFull = compsWithStats.find(c => c.id === competition.id) || competition;
      const competitionsForNiveau = compsWithStats.filter(c => c.niveau_id === niveau.id);

      // Mettre à jour les niveaux (on charge juste pour le breadcrumb)
      const niveauxRes = await api.get('/api/comp/niveaux');
      setNiveaux(niveauxRes.data);

      setSelectedNiveau(niveau);
      setSelectedCompetition(competitionFull);
      setSelectedEquipe(equipe);
      setCompetitions(competitionsForNiveau.length > 0 ? competitionsForNiveau : competitionsData);
      setEquipes(equipesData);

      if (joueursRes.data && joueursRes.data.joueurs) {
        setJoueurs(joueursRes.data.joueurs || []);
        setCedidosSortants(joueursRes.data.cedidos_sortants || []);
        setNcaaCommitsEntrants(joueursRes.data.ncaa_commits_entrants || []);
      } else {
        setJoueurs(joueursRes.data || []);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
      }
      setShowCedidos(false);
      setCurrentView('joueurs');
      setInitialNavigationDone(true);

      setNavigationHistory([
        { view: 'niveaux', item: niveau },
        { view: 'competitions', item: competitionFull },
        { view: 'equipes', item: equipe }
      ]);

      // Charger les données de recherche en arrière-plan
      loadAllData();

    } catch (error) {
      console.error('Error fast navigating to equipe:', error);
      // Fallback: charger normalement
      loadNiveaux();
      loadAllData();
    } finally {
      setLoading(false);
    }
  };

  // Gestion de la navigation par URL (fallback pour anciens liens avec niveau+competition)
  useEffect(() => {
    if (niveaux.length > 0 && !initialNavigationDone) {
      const urlParams = new URLSearchParams(location.search);
      const niveauId = urlParams.get('niveau');
      const competitionId = urlParams.get('competition');
      const equipeId = urlParams.get('equipe');

      // Branche virtuelle: deep-link vers la liste Free Agents
      if (isFreeAgentVirtualId(equipeId)) {
        handleSelectNiveau(virtualFreeAgentsNiveau);
        setInitialNavigationDone(true);
        return;
      }

      // Branche virtuelle: deep-link vers une sélection nationale
      if (isVirtualId(equipeId) && nationalSelectionsData) {
        navigateToVirtualEquipe(equipeId);
        setInitialNavigationDone(true);
        return;
      }

      // Si on a les 3 params et que fastNavigate n'a pas déjà géré
      if (niveauId && competitionId && equipeId && !isVirtualId(niveauId) && !isVirtualId(competitionId) && !isVirtualId(equipeId)) {
        navigateToEquipe(parseInt(niveauId), parseInt(competitionId), parseInt(equipeId));
        setInitialNavigationDone(true);
      }
    }
  }, [niveaux, location.search, initialNavigationDone, nationalSelectionsData]);

  // Navigation directe vers une équipe virtuelle (sélection nationale)
  const navigateToVirtualEquipe = async (equipeVirtualId) => {
    const parsed = parseVirtualId(equipeVirtualId);
    if (!parsed || parsed.kind !== 'equipe') return;

    const country = getVirtualCountry(parsed.countryKey);
    if (!country) return;

    const competitionVirtual = {
      id: buildVirtualCompetitionId(parsed.countryKey),
      nom: getCountryLabel(parsed.countryKey, t),
      niveau_id: VIRTUAL_NIVEAU_ID,
      virtual: true,
      countryKey: parsed.countryKey,
      flagCode: country.flagCode,
    };
    const equipesVirtual = buildVirtualEquipes(parsed.countryKey);
    const equipeVirtual = equipesVirtual.find(e => e.id === equipeVirtualId);
    if (!equipeVirtual) return;

    setSelectedNiveau(virtualNationalNiveau);
    setSelectedCompetition(competitionVirtual);
    setSelectedEquipe(equipeVirtual);
    setCompetitions(buildVirtualCompetitions());
    setEquipes(equipesVirtual);
    setNavigationHistory([
      { view: 'niveaux', item: virtualNationalNiveau },
      { view: 'competitions', item: competitionVirtual },
      { view: 'equipes', item: equipeVirtual }
    ]);
    setCurrentView('joueurs');
    await loadJoueursByEquipe(equipeVirtualId);
  };

  // Navigation automatique vers une équipe spécifique (fallback)
  const navigateToEquipe = async (niveauId, competitionId, equipeId) => {
    try {
      setLoading(true);

      // Trouver le niveau
      const niveau = niveaux.find(n => n.id === niveauId);
      if (!niveau) return;

      // Charger et définir les compétitions
      const competitionsRes = await api.get('/api/comp/competitions');
      const filteredCompetitions = competitionsRes.data.filter(c => c.niveau_id === niveauId);
      const competition = filteredCompetitions.find(c => c.id === competitionId);
      if (!competition) return;

      // Charger et définir les équipes
      const equipesRes = await api.get(`/api/comp/competitions/${competitionId}/equipes`);
      const equipe = equipesRes.data.find(e => e.id === equipeId);
      if (!equipe) return;

      // Charger les joueurs
      const joueursRes = await api.get(`/api/comp/equipes/${equipeId}/joueurs`);

      // Mettre à jour l'état
      setSelectedNiveau(niveau);
      setSelectedCompetition(competition);
      setSelectedEquipe(equipe);
      setCompetitions(filteredCompetitions);
      setEquipes(equipesRes.data);
      if (joueursRes.data && joueursRes.data.joueurs) {
        setJoueurs(joueursRes.data.joueurs || []);
        setCedidosSortants(joueursRes.data.cedidos_sortants || []);
        setNcaaCommitsEntrants(joueursRes.data.ncaa_commits_entrants || []);
      } else {
        setJoueurs(joueursRes.data || []);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
      }
      setShowCedidos(false);
      setCurrentView('joueurs');

      // Mettre à jour l'historique
      setNavigationHistory([
        { view: 'niveaux', item: niveau },
        { view: 'competitions', item: competition },
        { view: 'equipes', item: equipe }
      ]);

    } catch (error) {
      console.error('Error navigating to equipe:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Mettre à jour l'URL lors de la navigation
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedNiveau) params.set('niveau', selectedNiveau.id);
    if (selectedCompetition) params.set('competition', selectedCompetition.id);
    if (selectedEquipe) params.set('equipe', selectedEquipe.id);
    
    const newSearch = params.toString();
    if (newSearch && location.search !== `?${newSearch}`) {
      navigate(`${location.pathname}?${newSearch}`, { replace: true });
    } else if (!newSearch && location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [selectedNiveau, selectedCompetition, selectedEquipe, location, navigate]);
  
  // Mettre à jour l'URL quand la sélection change
  useEffect(() => {
    if (initialNavigationDone) {
      updateURL();
    }
  }, [selectedNiveau, selectedCompetition, selectedEquipe, updateURL, initialNavigationDone]);
  
  // Chargement des niveaux
  const loadNiveaux = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/comp/niveaux');
      setNiveaux(res.data);
    } catch (error) {
      console.error('Error cargando niveles:', error);
      alert(t('competitionsViewer.errors.loadingLevels'));
    } finally {
      setLoading(false);
    }
  };
  
  // Chargement des compétitions d'un niveau
  const loadCompetitionsByNiveau = async (niveauId) => {
    // Branche virtuelle: niveau sélections nationales -> liste de pays
    if (isVirtualId(niveauId)) {
      setCompetitions(buildVirtualCompetitions());
      return;
    }
    try {
      setLoading(true);
      const res = await api.get('/api/comp/competitions');
      const filtered = res.data.filter(c => c.niveau_id === niveauId);
      setCompetitions(filtered);
    } catch (error) {
      console.error('Error cargando competiciones:', error);
      alert(t('competitionsViewer.errors.loadingCompetitions'));
    } finally {
      setLoading(false);
    }
  };
  
  // Chargement des équipes d'une compétition
  const loadEquipesByCompetition = async (competitionId) => {
    // Branche virtuelle: compétition (pays) -> liste de catégories d'âge
    if (isVirtualId(competitionId)) {
      const parsed = parseVirtualId(competitionId);
      setEquipes(parsed?.countryKey ? buildVirtualEquipes(parsed.countryKey) : []);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/api/comp/competitions/${competitionId}/equipes`);
      setEquipes(res.data);
    } catch (error) {
      console.error('Error cargando equipos:', error);
      alert(t('competitionsViewer.errors.loadingTeams'));
    } finally {
      setLoading(false);
    }
  };
  
  // Chargement des joueurs d'une équipe
  const loadJoueursByEquipe = async (equipeId) => {
    // Branche virtuelle: Free Agents -> liste plate des joueurs is_free_agent=1
    if (isFreeAgentVirtualId(equipeId)) {
      try {
        setLoading(true);
        const res = await api.get('/api/comp/free-agents/joueurs');
        setJoueurs(res.data?.joueurs || []);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
        setShowCedidos(false);
      } catch (error) {
        console.error('Error cargando agentes libres:', error);
        setJoueurs([]);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
      } finally {
        setLoading(false);
      }
      return;
    }
    // Branche virtuelle: sélection nationale -> joueurs par selection_national
    if (isVirtualId(equipeId)) {
      const parsed = parseVirtualId(equipeId);
      if (!parsed || parsed.kind !== 'equipe') {
        setJoueurs([]);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
        return;
      }
      try {
        setLoading(true);
        const res = await api.get('/api/comp/national-selections/joueurs', {
          params: { countryKey: parsed.countryKey, age: parsed.ageCategory || '' }
        });
        if (res.data && res.data.joueurs) {
          setJoueurs(res.data.joueurs || []);
          setCedidosSortants(res.data.cedidos_sortants || []);
          setNcaaCommitsEntrants(res.data.ncaa_commits_entrants || []);
        } else {
          setJoueurs(res.data || []);
          setCedidosSortants([]);
          setNcaaCommitsEntrants([]);
        }
        setShowCedidos(false);
      } catch (error) {
        console.error('Error cargando jugadores selección nacional:', error);
        setJoueurs([]);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/api/comp/equipes/${equipeId}/joueurs`);
      // Nouvelle réponse: { joueurs: [...], cedidos_sortants: [...], ncaa_commits_entrants: [...] }
      if (res.data && res.data.joueurs) {
        setJoueurs(res.data.joueurs || []);
        setCedidosSortants(res.data.cedidos_sortants || []);
        setNcaaCommitsEntrants(res.data.ncaa_commits_entrants || []);
      } else {
        // Rétrocompatibilité si l'API retourne un array simple
        setJoueurs(res.data || []);
        setCedidosSortants([]);
        setNcaaCommitsEntrants([]);
      }
      setShowCedidos(false);
    } catch (error) {
      console.error('Error cargando jugadores:', error);
      setJoueurs([]);
      setCedidosSortants([]);
      setNcaaCommitsEntrants([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Navigation
  const handleSelectNiveau = (niveau) => {
    // Niveau virtuel "Free Agents" : pas de hiérarchie interne, on saute directement à la liste joueurs
    if (isFreeAgentVirtualId(niveau.id)) {
      const faPseudoEquipe = { id: niveau.id, nom: niveau.nom };
      setNavigationHistory([{ view: 'niveaux', item: niveau }]);
      setSelectedNiveau(niveau);
      setSelectedCompetition(faPseudoEquipe);
      setSelectedEquipe(faPseudoEquipe);
      setCurrentView('joueurs');
      setSlideDirection('left');
      loadJoueursByEquipe(niveau.id);
      setInitialNavigationDone(true);
      return;
    }
    setNavigationHistory([...navigationHistory, { view: 'niveaux', item: niveau }]);
    setSelectedNiveau(niveau);
    setCurrentView('competitions');
    setSlideDirection('left');
    loadCompetitionsByNiveau(niveau.id);
    setInitialNavigationDone(true);
  };
  
  const handleSelectCompetition = (competition) => {
    setNavigationHistory([...navigationHistory, { view: 'competitions', item: competition }]);
    setSelectedCompetition(competition);
    setCurrentView('equipes');
    setSlideDirection('left');
    loadEquipesByCompetition(competition.id);
  };
  
  const handleSelectEquipe = (equipe) => {
    setNavigationHistory([...navigationHistory, { view: 'equipes', item: equipe }]);
    setSelectedEquipe(equipe);
    setCurrentView('joueurs');
    setSlideDirection('left');
    loadJoueursByEquipe(equipe.id);
  };
  
  const handleBack = () => {
    setSlideDirection('right');

    // Pop l'historique d'abord, puis déduire la vue cible de sa nouvelle longueur.
    // Important pour les niveaux virtuels (Free Agents) qui sautent des étapes
    // à l'aller : l'historique a 1 entrée mais currentView = 'joueurs'.
    const newHistory = [...navigationHistory];
    newHistory.pop();

    const targetView = newHistory.length === 0 ? 'niveaux'
      : newHistory.length === 1 ? 'competitions'
      : newHistory.length === 2 ? 'equipes'
      : 'joueurs';

    if (targetView === 'niveaux') {
      setCurrentView('niveaux');
      setSelectedNiveau(null);
      setSelectedCompetition(null);
      setSelectedEquipe(null);
      setCompetitions([]);
      setEquipes([]);
      setJoueurs([]);
      setCedidosSortants([]);
      setNcaaCommitsEntrants([]);
      setShowCedidos(false);
    } else if (targetView === 'competitions') {
      setCurrentView('competitions');
      setSelectedCompetition(null);
      setSelectedEquipe(null);
      setEquipes([]);
      setJoueurs([]);
      setCedidosSortants([]);
      setNcaaCommitsEntrants([]);
      setShowCedidos(false);
    } else if (targetView === 'equipes') {
      setCurrentView('equipes');
      setSelectedEquipe(null);
      setJoueurs([]);
      setCedidosSortants([]);
      setNcaaCommitsEntrants([]);
      setShowCedidos(false);
    }

    setNavigationHistory(newHistory);
  };
  
  const handleHome = () => {
    setSlideDirection('right');
    setCurrentView('niveaux');
    setSelectedNiveau(null);
    setSelectedCompetition(null);
    setSelectedEquipe(null);
    setCompetitions([]);
    setEquipes([]);
    setJoueurs([]);
    setCedidosSortants([]);
    setNcaaCommitsEntrants([]);
    setShowCedidos(false);
    setNavigationHistory([]);
    navigate(location.pathname);
  };
  
  const navigateToBreadcrumb = (index) => {
    setSlideDirection('right');
    const targetHistory = navigationHistory.slice(0, index + 1);
    const target = targetHistory[targetHistory.length - 1];
    
    setNavigationHistory(targetHistory);
    
    if (target.view === 'niveaux') {
      setSelectedNiveau(target.item);
      setSelectedCompetition(null);
      setSelectedEquipe(null);
      setCurrentView('competitions');
      loadCompetitionsByNiveau(target.item.id);
    } else if (target.view === 'competitions') {
      setSelectedCompetition(target.item);
      setSelectedEquipe(null);
      setCurrentView('equipes');
      loadEquipesByCompetition(target.item.id);
    }
  };
  
  // Niveau virtuel "Sélections Nationales" ajouté à la fin de la liste
  const virtualNationalNiveau = {
    id: VIRTUAL_NIVEAU_ID,
    nom: t('competitionsViewer.nationalSelections.title'),
    ordre: (niveaux.length > 0 ? Math.max(...niveaux.map(n => n.ordre || 0)) : 0) + 1,
    virtual: true,
  };

  // Niveau virtuel "Free Agents" — sœur de Sélections Nationales, navigation directe vers la liste
  const virtualFreeAgentsNiveau = {
    id: VIRTUAL_NIVEAU_FA_ID,
    nom: t('competitionsViewer.freeAgents.title'),
    ordre: (niveaux.length > 0 ? Math.max(...niveaux.map(n => n.ordre || 0)) : 0) + 2,
    virtual: 'fa',
  };

  // Liste des niveaux à afficher = niveaux DB + niveaux virtuels
  const displayNiveaux = (() => {
    const result = [...niveaux];
    const hasNatData = nationalSelectionsData && nationalSelectionsData.countries && nationalSelectionsData.countries.length > 0;
    if (hasNatData) result.push(virtualNationalNiveau);
    // FA est toujours visible (même sans joueurs FA on veut le card cliquable, la liste sera vide)
    result.push(virtualFreeAgentsNiveau);
    return result;
  })();

  // Récupérer un pays par sa clé (depuis l'agrégat)
  const getVirtualCountry = (countryKey) => {
    if (!nationalSelectionsData) return null;
    return nationalSelectionsData.countries.find(c => c.countryKey === countryKey) || null;
  };

  // Construire la liste des "compétitions" (pays) virtuelles
  const buildVirtualCompetitions = () => {
    if (!nationalSelectionsData) return [];
    return nationalSelectionsData.countries.map(country => ({
      id: buildVirtualCompetitionId(country.countryKey),
      nom: getCountryLabel(country.countryKey, t),
      niveau_id: VIRTUAL_NIVEAU_ID,
      niveau_nom: t('competitionsViewer.nationalSelections.title'),
      nb_equipes: country.teams.length,
      nb_joueurs: country.total,
      virtual: true,
      countryKey: country.countryKey,
      flagCode: country.flagCode,
    }));
  };

  // Construire la liste des "équipes" (catégories d'âge) d'un pays virtuel
  const buildVirtualEquipes = (countryKey) => {
    const country = getVirtualCountry(countryKey);
    if (!country) return [];
    return country.teams.map(team => ({
      id: buildVirtualEquipeId(countryKey, team.ageCategory),
      nom: `${getCountryLabel(countryKey, t)}${team.ageCategory ? ` ${team.ageCategory}` : ` ${t('competitionsViewer.nationalSelections.senior')}`}`,
      nb_joueurs: team.nb,
      virtual: true,
      countryKey,
      ageCategory: team.ageCategory,
      flagCode: country.flagCode,
    }));
  };

  // Ouvrir le dialog "joueurs par passeport" pour une compétition / équipe / niveau.
  // Si `passport` est omis : tous les joueurs du scope (sans filtre passeport).
  // `kind` peut valoir 'cedidos' (droits) ou 'ncaa_commits' (engagements NCAA entrants).
  const openPassportDialog = async ({ passport, competitionId, equipeId, niveauId, scopeName, kind, titleSuffix }) => {
    const suffix = passport || titleSuffix;
    const title = suffix ? `${scopeName} - ${suffix}` : scopeName;
    return openPlayersDialogWithFetch(title, async () => {
      const params = {};
      if (passport) params.passport = passport;
      if (competitionId) params.competition_id = competitionId;
      if (equipeId) params.equipe_id = equipeId;
      if (niveauId) params.niveau_id = niveauId;
      if (kind) params.kind = kind;
      const res = await api.get('/api/comp/passport-players', { params });
      return res.data?.joueurs || [];
    });
  };

  // Ouvre le dialog des joueurs en utilisant une fonction de fetch personnalisée.
  // Utilisé pour les niveaux virtuels (sélections nationales, free agents) qui
  // n'utilisent pas l'endpoint passport-players.
  const openPlayersDialogWithFetch = async (title, fetchPlayers) => {
    setPassportDialogOpen(true);
    setPassportDialogLoading(true);
    setPassportDialogPlayers([]);
    setPassportDialogTitle(title);
    try {
      const players = await fetchPlayers();
      setPassportDialogPlayers(players || []);
    } catch (error) {
      console.error('Error loading players:', error);
      setPassportDialogPlayers([]);
    } finally {
      setPassportDialogLoading(false);
    }
  };

  const closePassportDialog = () => {
    setPassportDialogOpen(false);
    setPassportDialogPlayers([]);
    setPassportDialogTitle('');
  };

  // Fonction pour obtenir l'URL du logo (renvoie un drapeau pour les IDs virtuels)
  const getLogoUrl = (id, type) => {
    if (isVirtualId(id)) {
      const parsed = parseVirtualId(id);
      if (!parsed) return null;
      if (parsed.kind === 'niveau') return null;
      const country = getVirtualCountry(parsed.countryKey);
      return getCountryFlagUrl(country?.flagCode);
    }
    return `${mediaUrl}/${type}/${id}.jpg?t=${Date.now()}`;
  };
  
  // Fonction pour obtenir l'avatar d'un joueur
  const getPlayerAvatarUrl = (playerId) => {
    return playerId
      ? `${mediaUrl}/photos/${playerId}.png?${new Date().getTime()}`
      : null;
  };
  
  // Fonction pour calculer l'âge en décimal
  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return null;
    
    const birthDate = new Date(dateNaissance);
    const today = new Date();
    
    // Calculer la différence en années
    let age = today.getFullYear() - birthDate.getFullYear();
    
    // Calculer la partie décimale basée sur les mois et jours
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    
    // Ajuster si l'anniversaire n'est pas encore passé cette année
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    
    // Calculer la partie décimale (mois écoulés depuis le dernier anniversaire / 12)
    const lastBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    if (lastBirthday > today) {
      lastBirthday.setFullYear(today.getFullYear() - 1);
    }
    
    const daysSinceLastBirthday = Math.floor((today - lastBirthday) / (1000 * 60 * 60 * 24));
    const decimal = daysSinceLastBirthday / 365;
    
    return (age + decimal).toFixed(1);
  };
  
  // Rendu du breadcrumb avec bouton retour et filtre global
  const renderBreadcrumb = () => (
    <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5', position: 'relative' }}>
      <Stack direction="column" spacing={1.5}>
        {/* Filtre global toujours visible */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('competitionsViewer.searchPlaceholder')}
            value={globalSearchTerm}
            onChange={(e) => setGlobalSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: globalSearchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setGlobalSearchTerm('');
                      setShowSearchResults(false);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          {/* Résultats de recherche */}
          {showSearchResults && searchResults.length > 0 && (
            <Paper 
              sx={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                zIndex: 1000, 
                maxHeight: 400, 
                overflow: 'auto',
                mt: 0.5,
                boxShadow: 3
              }}
            >
              <List dense sx={{ p: 0 }}>
                {searchResults.map((result, index) => {
                  const typeStyle = (() => {
                    switch (result.type) {
                      case 'competition': return { color: '#F25F29', label: t('competitionsViewer.competition') };
                      case 'equipe':      return { color: '#1976d2', label: t('competitionsViewer.team') };
                      case 'joueur':      return { color: '#4caf50', label: t('competitionsViewer.player') };
                      case 'national_selection': return {
                        color: '#673ab7',
                        label: result.kind === 'team'
                          ? t('competitionsViewer.nationalSelections.selection')
                          : t('competitionsViewer.nationalSelections.country')
                      };
                      default: return { color: '#9e9e9e', label: result.type };
                    }
                  })();
                  const hexToRgba = (hex, a) => {
                    const v = hex.replace('#', '');
                    const bigint = parseInt(v.length === 3 ? v.split('').map(c => c + c).join('') : v, 16);
                    return `rgba(${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}, ${a})`;
                  };

                  return (
                  <ListItem
                    key={`${result.type}-${result.id}`}
                    button
                    onClick={() => handleSearchResultClick(result)}
                    sx={{
                      '&:hover': { backgroundColor: hexToRgba(typeStyle.color, 0.08) },
                      borderLeft: '4px solid',
                      borderLeftColor: typeStyle.color,
                      borderBottom: index < searchResults.length - 1 ? '1px solid' : 'none',
                      borderBottomColor: 'divider',
                      py: 1.5
                    }}
                  >
                    <ListItemAvatar>
                      {result.type === 'joueur' ? (
                        <Avatar
                          src={getPlayerAvatarUrl(result.id)}
                          sx={{
                            width: 40,
                            height: 40,
                            border: '2px solid #4caf50'
                          }}
                        >
                          {result.nom?.charAt(0)}
                        </Avatar>
                      ) : (
                        <Box
                          component="img"
                          src={getLogoUrl(result.id, result.type === 'competition' ? 'competitions' : 'equipes')}
                          alt={result.nom}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: '2px solid',
                            borderColor: typeStyle.color,
                            backgroundColor: 'white',
                            p: 0.5
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            // Afficher un avatar de fallback
                            const fallbackAvatar = document.createElement('div');
                            fallbackAvatar.style.width = '40px';
                            fallbackAvatar.style.height = '40px';
                            fallbackAvatar.style.borderRadius = '4px';
                            fallbackAvatar.style.backgroundColor = typeStyle.color;
                            fallbackAvatar.style.display = 'flex';
                            fallbackAvatar.style.alignItems = 'center';
                            fallbackAvatar.style.justifyContent = 'center';
                            fallbackAvatar.style.color = 'white';
                            fallbackAvatar.style.fontWeight = 'bold';
                            fallbackAvatar.textContent = result.type === 'competition' ? 'C' :
                                                         result.type === 'national_selection' ? 'N' : 'E';
                            e.target.parentNode.appendChild(fallbackAvatar);
                          }}
                        />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {result.nom}
                          {result.type === 'joueur' && result.annee_naissance && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              ({result.annee_naissance})
                            </Typography>
                          )}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={typeStyle.label}
                            size="small"
                            sx={{
                              backgroundColor: hexToRgba(typeStyle.color, 0.2),
                              color: typeStyle.color,
                              fontWeight: 'bold',
                              height: 20
                            }}
                          />
                          {result.type === 'national_selection' && result.nb_joueurs !== undefined && (
                            <Chip
                              label={t('competitionsViewer.playerCount', { count: result.nb_joueurs })}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                          {result.type === 'joueur' && result.poste && (
                            <Chip
                              label={result.poste}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                          {result.type === 'joueur' && result.nationalite && (
                            <Chip
                              label={result.nationalite}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                          {result.niveau_noms && (
                            <Chip
                              label={result.niveau_noms}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                          {result.type === 'equipe' && result.competition_nom && (
                            <Tooltip title={t('competitionsViewer.competition')}>
                              <Chip
                                label={result.competition_nom}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            </Tooltip>
                          )}
                          {result.type === 'joueur' && result.equipe_noms && (
                            <Chip
                              label={result.equipe_noms}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}
          
          {showSearchResults && searchResults.length === 0 && globalSearchTerm.length >= 2 && (
            <Paper 
              sx={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                zIndex: 1000,
                p: 2,
                mt: 0.5,
                boxShadow: 3
              }}
            >
              <Typography color="text.secondary" align="center">
                {t('competitionsViewer.noResults', { term: globalSearchTerm })}
              </Typography>
            </Paper>
          )}
        </Box>
        
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            underline="hover"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              color: currentView === 'niveaux' ? 'text.primary' : '#F25F29' 
            }}
            onClick={handleHome}
          >
            <HomeIcon sx={{ mr: 0.5, color: '#F25F29' }} fontSize="small" />
            {t('competitionsViewer.levels')}
          </Link>
          
          {navigationHistory.map((item, index) => {
            const isLast = index === navigationHistory.length - 1;
            const isCompetition = item.view === 'competitions';
            const isEquipe = item.view === 'equipes';
            
            return (
              <Link
                key={index}
                underline={isLast ? "none" : "hover"}
                sx={{ 
                  cursor: isLast ? 'default' : 'pointer',
                  color: isLast ? 'text.primary' : '#F25F29',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
                onClick={() => !isLast && navigateToBreadcrumb(index)}
              >
                {(isCompetition || isEquipe) && !isVirtualId(item.item.id) && (
                  <Box
                    component="img"
                    src={getLogoUrl(item.item.id, isCompetition ? 'competitions' : 'equipes')}
                    alt={item.item.nom}
                    sx={{
                      width: 20,
                      height: 20,
                      objectFit: 'contain',
                      borderRadius: '2px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                {item.item.nom}
              </Link>
            );
          })}
          </Breadcrumbs>
          
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Logos de la compétition et équipe actuellement sélectionnées (pas pour les niveaux virtuels) */}
            {selectedCompetition && !isVirtualId(selectedCompetition.id) && (
              <Box
                component="img"
                src={getLogoUrl(selectedCompetition.id, 'competitions')}
                alt={selectedCompetition.nom}
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: 'contain',
                  borderRadius: '4px',
                  border: '2px solid #F25F29',
                  p: 0.25,
                  backgroundColor: 'white'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}

            {selectedEquipe && !isVirtualId(selectedEquipe.id) && (
              <Box
                component="img"
                src={getLogoUrl(selectedEquipe.id, 'equipes')}
                alt={selectedEquipe.nom}
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: 'contain',
                  borderRadius: '4px',
                  border: '2px solid #1976d2',
                  p: 0.25,
                  backgroundColor: 'white'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            
            {currentView !== 'niveaux' && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
                size="small"
                sx={{ 
                  borderColor: '#F25F29',
                  color: '#F25F29',
                  '&:hover': {
                    borderColor: '#F25F29',
                    backgroundColor: 'rgba(242, 95, 41, 0.08)'
                  }
                }}
              >
                {t('competitionsViewer.back')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
  
  // Rendu des niveaux avec les compétitions
  const renderNiveaux = () => {
    // Grouper les compétitions par niveau
    const competitionsByNiveau = {};
    allCompetitions.forEach(comp => {
      if (!competitionsByNiveau[comp.niveau_id]) {
        competitionsByNiveau[comp.niveau_id] = [];
      }
      competitionsByNiveau[comp.niveau_id].push(comp);
    });
    
    return (
      <Fade in={currentView === 'niveaux'} timeout={300}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            <TrophyIcon sx={{ mr: 1, verticalAlign: 'bottom', color: '#F25F29' }} />
            {t('competitionsViewer.title')}
          </Typography>
          
          <Grid container spacing={{ xs: 2, lg: 1 }}>
            {displayNiveaux.map((niveau) => {
              const isVirtualNiveau = niveau.virtual === true;
              const isVirtualFa = niveau.virtual === 'fa';
              // Pour le niveau virtuel national: top 12 pays par nb joueurs.
              // Pour le niveau virtuel FA: pas de sous-niveaux (clic = direct vers liste).
              // Pour les niveaux DB: les compétitions du niveau (comportement existant).
              const niveauCompetitions = isVirtualNiveau
                ? (nationalSelectionsData?.countries || []).slice(0, 12).map(c => ({
                    id: buildVirtualCompetitionId(c.countryKey),
                    nom: getCountryLabel(c.countryKey, t),
                    niveau_id: VIRTUAL_NIVEAU_ID,
                    virtual: true,
                    countryKey: c.countryKey,
                    flagCode: c.flagCode,
                  }))
                : isVirtualFa
                  ? []
                  : (competitionsByNiveau[niveau.id] || []);

              return (
                <Grid item xs={12} sm={6} md={3} lg={12 / 9} xl={12 / 9} key={niveau.id}>
                  <Card
                    sx={{
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: 4
                      },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      p: { xs: 1.5, lg: 0.75 },
                      background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                    }}
                  >
                    {/* En-tête cliquable pour accéder au niveau */}
                    <Box
                      onClick={() => handleSelectNiveau(niveau)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1.5,
                        pb: 0.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        minHeight: 48,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          mr: 1,
                          flexShrink: 0,
                          bgcolor: isVirtualFa ? '#4caf50' : (isVirtualNiveau ? '#1976d2' : '#F25F29'),
                          fontSize: '1rem'
                        }}
                      >
                        {isVirtualFa ? <PersonIcon fontSize="small" /> : (isVirtualNiveau ? <LanguageIcon fontSize="small" /> : niveau.ordre)}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.85rem',
                          lineHeight: 1.2,
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {niveau.nom}
                      </Typography>
                    </Box>

                    {/* Chip "tous les joueurs" — ouvre la dialog avec tous les joueurs du niveau */}
                    {(() => {
                      // Nombre de joueurs + handler de fetch selon le type de niveau.
                      let totalPlayers = 0;
                      let onClickHandler = null;
                      if (isVirtualNiveau) {
                        // Sélections nationales : somme des totaux par pays
                        totalPlayers = (nationalSelectionsData?.countries || [])
                          .reduce((sum, c) => sum + (c.total || 0), 0);
                        onClickHandler = () => openPlayersDialogWithFetch(niveau.nom, async () => {
                          const res = await api.get('/api/comp/national-selections/joueurs');
                          return res.data?.joueurs || [];
                        });
                      } else if (isVirtualFa) {
                        // Free Agents : count exposé par l'agrégat des sélections nationales
                        totalPlayers = nationalSelectionsData?.freeAgentsCount || 0;
                        onClickHandler = () => openPlayersDialogWithFetch(niveau.nom, async () => {
                          const res = await api.get('/api/comp/free-agents/joueurs');
                          return res.data?.joueurs || [];
                        });
                      } else if (niveauCompetitions.length > 0) {
                        totalPlayers = niveauCompetitions.reduce(
                          (sum, c) => sum + (c.nb_joueurs || 0),
                          0
                        );
                        onClickHandler = () => openPassportDialog({
                          niveauId: niveau.id,
                          scopeName: niveau.nom,
                        });
                      }
                      if (totalPlayers === 0 || !onClickHandler) return null;
                      return (
                        <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
                          <Chip
                            icon={<PersonIcon sx={{ fontSize: '0.65rem !important' }} />}
                            label={t('competitionsViewer.playerCount', { count: totalPlayers })}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onClickHandler();
                            }}
                            sx={{
                              fontSize: '0.6rem',
                              height: 16,
                              cursor: 'pointer',
                              backgroundColor: '#f0f0f0',
                              fontWeight: 'bold',
                              '&:hover': { filter: 'brightness(0.92)' },
                              '& .MuiChip-icon': { ml: 0.3 }
                            }}
                          />
                        </Stack>
                      );
                    })()}

                    {/* Liste des compétitions avec logos */}
                    <Box sx={{ flex: 1 }}>
                      {niveauCompetitions.length > 0 ? (
                        <Grid container spacing={0.3}>
                          {niveauCompetitions.map(comp => (
                            <Grid item xs={6} key={comp.id}>
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Naviguer directement vers la compétition
                                  setSelectedNiveau(niveau);
                                  setSelectedCompetition(comp);
                                  setCurrentView('equipes');
                                  setNavigationHistory([
                                    { view: 'niveaux', item: niveau },
                                    { view: 'competitions', item: comp }
                                  ]);
                                  loadEquipesByCompetition(comp.id);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  p: 0.3,
                                  borderRadius: 1,
                                  '&:hover': {
                                    backgroundColor: 'rgba(242, 95, 41, 0.08)'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={getLogoUrl(comp.id, 'competitions')}
                                  alt={comp.nom}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    objectFit: 'contain',
                                    mb: 0.75,
                                    borderRadius: '2px'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <Typography 
                                  variant="caption" 
                                  align="center"
                                  sx={{ 
                                    fontSize: '0.65rem',
                                    lineHeight: 1.1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }}
                                >
                                  {comp.nom}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      ) : isVirtualFa ? (
                        <Box
                          onClick={() => handleSelectNiveau(niveau)}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1.5,
                            borderRadius: 1,
                            color: '#4caf50',
                            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' }
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 32, mb: 0.5 }} />
                          <Typography variant="caption" align="center" sx={{ fontWeight: 'medium' }}>
                            {t('competitionsViewer.freeAgents.viewList')}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          {t('competitionsViewer.noCompetitions')}
                        </Typography>
                      )}
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Fade>
    );
  };
  
  // Filtrer les compétitions de la vue actuelle
  const getFilteredCompetitions = () => {
    return competitions;
  };
  
  // Filtrer les équipes de la vue actuelle
  const getFilteredEquipes = () => {
    return equipes;
  };
  
  // Recherche globale
  useEffect(() => {
    if (globalSearchTerm.length >= 2) {
      const searchTerm = globalSearchTerm.toLowerCase();
      const results = [];

      // Rechercher dans les compétitions
      allCompetitions.forEach(comp => {
        const compName = comp.nom.toLowerCase();
        if (compName.includes(searchTerm)) {
          results.push({
            type: 'competition',
            id: comp.id,
            nom: comp.nom,
            niveau_id: comp.niveau_id,
            niveau_nom: comp.niveau_nom,
            data: comp,
            // Ajouter un score de pertinence: 2 si commence par le terme, 1 sinon
            relevanceScore: compName.startsWith(searchTerm) ? 2 : 1,
            // Position du terme dans le nom (pour départager)
            position: compName.indexOf(searchTerm)
          });
        }
      });

      // Rechercher dans les équipes
      allEquipes.forEach(equipe => {
        const equipeName = equipe.nom.toLowerCase();
        if (equipeName.includes(searchTerm)) {
          results.push({
            type: 'equipe',
            id: equipe.id,
            nom: equipe.nom,
            competition_id: equipe.competition_id,
            competition_nom: equipe.competition_nom,
            niveau_id: equipe.niveau_id,
            niveau_nom: equipe.niveau_nom,
            data: equipe,
            // Ajouter un score de pertinence: 2 si commence par le terme, 1 sinon
            relevanceScore: equipeName.startsWith(searchTerm) ? 2 : 1,
            // Position du terme dans le nom (pour départager)
            position: equipeName.indexOf(searchTerm)
          });
        }
      });

      // Rechercher dans les sélections nationales (virtuelles)
      if (nationalSelectionsData) {
        const natTitle = t('competitionsViewer.nationalSelections.title');
        nationalSelectionsData.countries.forEach(country => {
          const countryLabel = getCountryLabel(country.countryKey, t);
          const countryLower = countryLabel.toLowerCase();

          // Match sur le pays lui-même
          if (countryLower.includes(searchTerm) || country.countryKey.toLowerCase().includes(searchTerm)) {
            results.push({
              type: 'national_selection',
              kind: 'country',
              id: buildVirtualCompetitionId(country.countryKey),
              nom: countryLabel,
              countryKey: country.countryKey,
              flagCode: country.flagCode,
              niveau_noms: natTitle,
              nb_joueurs: country.total,
              relevanceScore: countryLower.startsWith(searchTerm) ? 2 : 1,
              position: countryLower.indexOf(searchTerm) >= 0 ? countryLower.indexOf(searchTerm) : 0,
            });
          }

          // Match sur les équipes spécifiques (Senior, U17, ...)
          country.teams.forEach(team => {
            const teamLabel = `${countryLabel}${team.ageCategory ? ` ${team.ageCategory}` : ` ${t('competitionsViewer.nationalSelections.senior')}`}`;
            const teamLower = teamLabel.toLowerCase();
            if (teamLower.includes(searchTerm)) {
              results.push({
                type: 'national_selection',
                kind: 'team',
                id: buildVirtualEquipeId(country.countryKey, team.ageCategory),
                nom: teamLabel,
                countryKey: country.countryKey,
                ageCategory: team.ageCategory,
                flagCode: country.flagCode,
                niveau_noms: natTitle,
                nb_joueurs: team.nb,
                relevanceScore: teamLower.startsWith(searchTerm) ? 2 : 1,
                position: teamLower.indexOf(searchTerm),
              });
            }
          });
        });
      }

      // Rechercher dans les joueurs
      allJoueurs.forEach(joueur => {
        const joueurName = joueur.nom.toLowerCase();
        if (joueurName.includes(searchTerm)) {
          // Prendre seulement la première équipe et compétition si le joueur en a plusieurs
          const firstEquipeId = joueur.equipe_ids && joueur.equipe_ids.length > 0 ? joueur.equipe_ids[0] : null;
          const firstCompetitionId = joueur.competition_ids && joueur.competition_ids.length > 0 ? joueur.competition_ids[0] : null;
          const firstNiveauId = joueur.niveau_ids && joueur.niveau_ids.length > 0 ? joueur.niveau_ids[0] : null;

          results.push({
            type: 'joueur',
            id: joueur.id,
            nom: joueur.nom,
            equipe_id: firstEquipeId,
            equipe_noms: joueur.equipe_noms,
            competition_id: firstCompetitionId,
            competition_noms: joueur.competition_noms,
            niveau_id: firstNiveauId,
            niveau_noms: joueur.niveau_noms,
            poste: joueur.poste,
            nationalite: joueur.nationalite,
            annee_naissance: joueur.annee_naissance,
            data: joueur,
            // Ajouter un score de pertinence: 2 si commence par le terme, 1 sinon
            relevanceScore: joueurName.startsWith(searchTerm) ? 2 : 1,
            // Position du terme dans le nom (pour départager)
            position: joueurName.indexOf(searchTerm)
          });
        }
      });

      // Trier les résultats par pertinence:
      // 1. D'abord par score de pertinence (startsWith > contains)
      // 2. Ensuite par position (plus tôt dans le nom = mieux)
      // 3. Finalement par nom alphabétique
      results.sort((a, b) => {
        // D'abord par score de pertinence
        if (a.relevanceScore !== b.relevanceScore) {
          return b.relevanceScore - a.relevanceScore; // Score plus élevé en premier
        }
        // Ensuite par position du terme
        if (a.position !== b.position) {
          return a.position - b.position; // Position plus proche du début en premier
        }
        // Finalement par ordre alphabétique
        return a.nom.localeCompare(b.nom);
      });

      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [globalSearchTerm, allCompetitions, allEquipes, allJoueurs, nationalSelectionsData, t]);
  
  // Naviguer vers un résultat de recherche
  const handleSearchResultClick = async (result) => {
    setGlobalSearchTerm('');
    setShowSearchResults(false);

    if (result.type === 'national_selection') {
      // Sélection nationale virtuelle: pays ou équipe (catégorie d'âge)
      const country = getVirtualCountry(result.countryKey);
      if (!country) return;
      const competitionVirtual = {
        id: buildVirtualCompetitionId(result.countryKey),
        nom: getCountryLabel(result.countryKey, t),
        niveau_id: VIRTUAL_NIVEAU_ID,
        virtual: true,
        countryKey: result.countryKey,
        flagCode: country.flagCode,
      };

      if (result.kind === 'country') {
        setSelectedNiveau(virtualNationalNiveau);
        setSelectedCompetition(competitionVirtual);
        setSelectedEquipe(null);
        setCompetitions(buildVirtualCompetitions());
        setEquipes(buildVirtualEquipes(result.countryKey));
        setCurrentView('equipes');
        setNavigationHistory([
          { view: 'niveaux', item: virtualNationalNiveau },
          { view: 'competitions', item: competitionVirtual }
        ]);
      } else {
        await navigateToVirtualEquipe(result.id);
      }
      return;
    }

    if (result.type === 'competition') {
      // Naviguer vers la compétition
      const niveau = niveaux.find(n => n.id === result.niveau_id);
      if (niveau) {
        setSelectedNiveau(niveau);
        setSelectedCompetition(result.data);
        setCurrentView('equipes');
        setNavigationHistory([
          { view: 'niveaux', item: niveau },
          { view: 'competitions', item: result.data }
        ]);
        await loadEquipesByCompetition(result.id);
      }
    } else if (result.type === 'equipe') {
      // Naviguer vers l'équipe
      const niveau = niveaux.find(n => n.id === result.niveau_id);
      const competition = allCompetitions.find(c => c.id === result.competition_id);

      if (niveau && competition) {
        setSelectedNiveau(niveau);
        setSelectedCompetition(competition);
        setSelectedEquipe(result.data);
        setCurrentView('joueurs');
        setNavigationHistory([
          { view: 'niveaux', item: niveau },
          { view: 'competitions', item: competition },
          { view: 'equipes', item: result.data }
        ]);
        await loadEquipesByCompetition(result.competition_id);
        await loadJoueursByEquipe(result.id);
      }
    } else if (result.type === 'joueur') {
      // Naviguer vers le joueur
      // Si le joueur a une équipe, naviguer vers son équipe
      if (result.equipe_id) {
        const niveau = niveaux.find(n => n.id === result.niveau_id);
        const competition = allCompetitions.find(c => c.id === result.competition_id);
        const equipe = allEquipes.find(e => e.id === result.equipe_id);

        if (niveau && competition && equipe) {
          setSelectedNiveau(niveau);
          setSelectedCompetition(competition);
          setSelectedEquipe(equipe);
          setCurrentView('joueurs');
          setNavigationHistory([
            { view: 'niveaux', item: niveau },
            { view: 'competitions', item: competition },
            { view: 'equipes', item: equipe }
          ]);

          // Charger les équipes de la compétition
          await loadEquipesByCompetition(result.competition_id);
          // Charger les joueurs de l'équipe
          await loadJoueursByEquipe(result.equipe_id);

          // Après un court délai, faire défiler jusqu'au joueur et le mettre en surbrillance
          setTimeout(() => {
            const playerElement = document.getElementById(`player-${result.id}`);
            if (playerElement) {
              playerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Ajouter une animation de surbrillance temporaire
              playerElement.style.backgroundColor = 'rgba(76, 175, 80, 0.2)';
              playerElement.style.transition = 'background-color 0.5s';
              setTimeout(() => {
                playerElement.style.backgroundColor = '';
              }, 2000);
            }
          }, 500);
        }
      } else {
        // Si le joueur n'a pas d'équipe, ouvrir sa page directement
        window.open(`/players/${result.id}`, '_blank');
      }
    }
  };
  
  // Rendu des compétitions
  const renderCompetitions = () => {
    const filteredCompetitions = getFilteredCompetitions();
    
    return (
      <Slide direction={slideDirection} in={currentView === 'competitions'} mountOnEnter unmountOnExit>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            <TrophyIcon sx={{ mr: 1, verticalAlign: 'bottom', color: '#F25F29' }} />
            {t('competitionsViewer.competitions')} - {selectedNiveau?.nom}
          </Typography>
          
          <Grid container spacing={2}>
            {filteredCompetitions.map((competition) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={competition.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 4
                  },
                  height: '100%',
                }}
                onClick={() => handleSelectCompetition(competition)}
              >
                {isVirtualId(competition.id) ? (
                  <Box
                    sx={{
                      height: 90,
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(competition.id, 'competitions')}
                      alt={competition.nom}
                      sx={{
                        maxHeight: 56,
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height="90"
                    image={getLogoUrl(competition.id, 'competitions')}
                    alt={competition.nom}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{ objectFit: 'contain', p: 0.3, backgroundColor: '#f5f5f5' }}
                  />
                )}
                <CardContent sx={{ p: 0.5, pb: 0.8, '&:last-child': { pb: 0.8 } }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.3} sx={{ mb: 0.3 }}>
                    <Typography variant="caption" align="center" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                      {competition.nom}
                    </Typography>
                    {competition.eurobasket_url && (
                      <IconButton
                        size="small"
                        href={competition.eurobasket_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={competition.eurobasket_url}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0.2, color: '#F25F29' }}
                      >
                        <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack direction="column" spacing={0.5}>
                    {/* Première ligne: équipes et niveau */}
                    <Stack direction="row" justifyContent="center" spacing={0.3} sx={{ flexWrap: 'wrap', gap: 0.3 }}>
                      {competition.nb_equipes !== undefined && competition.nb_equipes !== null && (
                        <Chip
                          label={t('competitionsViewer.teamCount', { count: competition.nb_equipes })}
                          size="small"
                          color="success"
                          sx={{ fontSize: '0.6rem', height: 16, fontWeight: 'bold' }}
                        />
                      )}
                      <Chip 
                        label={competition.niveau_nom} 
                        size="small" 
                        color="primary"
                        sx={{ fontSize: '0.6rem', height: 16 }}
                      />
                    </Stack>
                    
                    {/* Deuxième ligne: total joueurs */}
                    {competition.nb_joueurs !== undefined && competition.nb_joueurs > 0 && (
                      <Stack direction="row" justifyContent="center">
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: '0.65rem !important' }} />}
                          label={t('competitionsViewer.playerCount', { count: competition.nb_joueurs })}
                          size="small"
                          color="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Compétition virtuelle (pays sélection nationale) : utiliser l'endpoint dédié
                            if (competition.virtual && competition.countryKey) {
                              openPlayersDialogWithFetch(competition.nom, async () => {
                                const res = await api.get('/api/comp/national-selections/joueurs', {
                                  params: { countryKey: competition.countryKey }
                                });
                                return res.data?.joueurs || [];
                              });
                            } else {
                              openPassportDialog({
                                competitionId: competition.id,
                                scopeName: competition.nom,
                              });
                            }
                          }}
                          sx={{
                            fontSize: '0.6rem',
                            height: 16,
                            backgroundColor: '#f0f0f0',
                            cursor: 'pointer',
                            '&:hover': { filter: 'brightness(0.92)' }
                          }}
                        />
                      </Stack>
                    )}
                    
                    {/* Troisième ligne: répartition des passeports + cedidos */}
                    {((competition.passport_distribution && Object.keys(competition.passport_distribution).length > 0) || (competition.id && !competition.virtual && competition.nb_cedidos > 0)) && (
                      <Stack direction="row" justifyContent="center" spacing={0.2} sx={{ flexWrap: 'wrap', gap: 0.2 }}>
                        {Object.entries(competition.passport_distribution || {})
                          .filter(([passport, count]) => passport && passport !== 'NO_PASSPORT' && count > 0)
                          .sort(([a], [b]) => compareLicencias(a, b))
                          .map(([passport, count], index) => {
                            // Couleurs pour les différents passeports (jusqu'à 5)
                            const passportColors = ['#ffecb3', '#e1f5fe', '#f3e5f5', '#e8f5e9', '#fce4ec'];
                            return (
                              <Chip
                                key={passport}
                                icon={<LanguageIcon sx={{ fontSize: '0.55rem !important' }} />}
                                label={`${count} ${passport}`}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPassportDialog({
                                    passport,
                                    competitionId: competition.id,
                                    scopeName: competition.nom,
                                  });
                                }}
                                sx={{
                                  fontSize: '0.55rem',
                                  height: 14,
                                  cursor: 'pointer',
                                  backgroundColor: passportColors[index % passportColors.length],
                                  '&:hover': { filter: 'brightness(0.92)' },
                                  '& .MuiChip-icon': {
                                    fontSize: '0.55rem',
                                    ml: 0.3
                                  }
                                }}
                              />
                            );
                          })}
                        {competition.id && !competition.virtual && competition.nb_cedidos > 0 && (
                          <Chip
                            icon={<PersonIcon sx={{ fontSize: '0.55rem !important' }} />}
                            label={t('competitionsViewer.cedidos.count', { count: competition.nb_cedidos })}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPassportDialog({
                                competitionId: competition.id,
                                scopeName: competition.nom,
                                kind: 'cedidos',
                                titleSuffix: t('competitionsViewer.cedidos.title'),
                              });
                            }}
                            sx={{
                              fontSize: '0.55rem',
                              height: 14,
                              cursor: 'pointer',
                              backgroundColor: '#fff3e0',
                              '&:hover': { filter: 'brightness(0.92)' },
                              '& .MuiChip-icon': {
                                fontSize: '0.55rem',
                                ml: 0.3
                              }
                            }}
                          />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Slide>
    );
  };

  // Rendu des équipes
  const renderEquipes = () => {
    const filteredEquipes = getFilteredEquipes();
    
    return (
      <Slide direction={slideDirection} in={currentView === 'equipes'} mountOnEnter unmountOnExit>
        <Box>
          <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mb: 3, rowGap: 1, columnGap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}>
              <TeamsIcon sx={{ mr: 1, verticalAlign: 'bottom', color: '#F25F29' }} />
              {t('competitionsViewer.teams')} - {selectedCompetition?.nom} ({t('competitionsViewer.teamCount', { count: filteredEquipes.length })})
            </Typography>
            {selectedCompetition?.eurobasket_url && (
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={selectedCompetition.eurobasket_url}
                target="_blank"
                rel="noopener noreferrer"
                title={selectedCompetition.eurobasket_url}
                sx={{
                  backgroundColor: '#F25F29',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#d94e1f' }
                }}
              >
                Eurobasket
              </Button>
            )}
            {(() => {
              const totalPlayers = selectedCompetition?.nb_joueurs
                ?? filteredEquipes.reduce((sum, e) => sum + (e.nb_joueurs || 0), 0);
              if (!totalPlayers || !selectedCompetition?.id) return null;
              const isVirtualCompetition = selectedCompetition.virtual && selectedCompetition.countryKey;
              return (
                <Chip
                  icon={<PersonIcon sx={{ fontSize: '0.8rem !important' }} />}
                  label={t('competitionsViewer.playerCount', { count: totalPlayers })}
                  size="small"
                  onClick={() => {
                    if (isVirtualCompetition) {
                      openPlayersDialogWithFetch(selectedCompetition.nom, async () => {
                        const res = await api.get('/api/comp/national-selections/joueurs', {
                          params: { countryKey: selectedCompetition.countryKey }
                        });
                        return res.data?.joueurs || [];
                      });
                    } else {
                      openPassportDialog({
                        competitionId: selectedCompetition.id,
                        scopeName: selectedCompetition.nom,
                      });
                    }
                  }}
                  sx={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    backgroundColor: '#f0f0f0',
                    '&:hover': { filter: 'brightness(0.92)' },
                    '& .MuiChip-icon': { ml: 0.5 }
                  }}
                />
              );
            })()}
            {selectedCompetition?.id && !selectedCompetition?.virtual && selectedCompetition?.nb_ncaa_commits > 0 && (
              <Chip
                icon={<PersonIcon sx={{ fontSize: '0.8rem !important' }} />}
                label={t('competitionsViewer.ncaaCommits.count', { count: selectedCompetition.nb_ncaa_commits })}
                size="small"
                onClick={() => openPassportDialog({
                  competitionId: selectedCompetition.id,
                  scopeName: selectedCompetition.nom,
                  kind: 'ncaa_commits',
                  titleSuffix: t('competitionsViewer.ncaaCommits.title'),
                })}
                sx={{
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  backgroundColor: '#e3f2fd',
                  '&:hover': { filter: 'brightness(0.92)' },
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            )}
            {/\bacb\b/i.test(selectedCompetition?.nom || '') && selectedCompetition?.nb_tanteo > 0 && (
              <Chip
                icon={<GavelIcon sx={{ fontSize: '0.8rem !important' }} />}
                label={`${selectedCompetition.nb_tanteo} Tanteo`}
                size="small"
                onClick={() => openPlayersDialogWithFetch('JUGADORES SUJETOS DERECHO TANTEO', async () => {
                  const res = await api.get('/api/comp/tanteo-players');
                  return res.data?.joueurs || [];
                })}
                sx={{
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  backgroundColor: '#fff8e1',
                  '&:hover': { filter: 'brightness(0.92)' },
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            )}
            {selectedCompetition?.passport_distribution && Object.keys(selectedCompetition.passport_distribution).length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(selectedCompetition.passport_distribution)
                  .filter(([passport, count]) => passport && passport !== 'NO_PASSPORT' && count > 0)
                  .sort(([a], [b]) => compareLicencias(a, b))
                  .map(([passport, count], index) => {
                    const passportColors = ['#ffecb3', '#e1f5fe', '#f3e5f5', '#e8f5e9', '#fce4ec'];
                    return (
                      <Chip
                        key={passport}
                        icon={<LanguageIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label={`${count} ${passport}`}
                        size="small"
                        onClick={() => openPassportDialog({
                          passport,
                          competitionId: selectedCompetition.id,
                          scopeName: selectedCompetition.nom,
                        })}
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          backgroundColor: passportColors[index % passportColors.length],
                          '&:hover': { filter: 'brightness(0.92)' },
                          '& .MuiChip-icon': { ml: 0.5 }
                        }}
                      />
                    );
                  })}
              </Stack>
            )}
            {selectedCompetition?.id && !selectedCompetition?.virtual && selectedCompetition?.nb_cedidos > 0 && (
              <Chip
                icon={<PersonIcon sx={{ fontSize: '0.8rem !important' }} />}
                label={t('competitionsViewer.cedidos.count', { count: selectedCompetition.nb_cedidos })}
                size="small"
                onClick={() => openPassportDialog({
                  competitionId: selectedCompetition.id,
                  scopeName: selectedCompetition.nom,
                  kind: 'cedidos',
                  titleSuffix: t('competitionsViewer.cedidos.title'),
                })}
                sx={{
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  backgroundColor: '#fff3e0',
                  '&:hover': { filter: 'brightness(0.92)' },
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            )}
          </Stack>

          <Grid container spacing={2}>
            {filteredEquipes.map((equipe) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={equipe.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 4
                  },
                  height: '100%',
                }}
                onClick={() => handleSelectEquipe(equipe)}
              >
                {isVirtualId(equipe.id) ? (
                  <Box
                    sx={{
                      height: 90,
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipe.id, 'equipes')}
                      alt={equipe.nom}
                      sx={{
                        maxHeight: 56,
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height="90"
                    image={getLogoUrl(equipe.id, 'equipes')}
                    alt={equipe.nom}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{ objectFit: 'contain', p: 0.3, backgroundColor: '#f5f5f5' }}
                  />
                )}
                <CardContent sx={{ p: 0.5, pb: 0.8, '&:last-child': { pb: 0.8 } }}>
                  <Typography variant="caption" gutterBottom align="center" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                    {equipe.nom}
                  </Typography>
                  <Stack direction="column" spacing={0.5}>
                    {/* Première ligne: nombre de joueurs */}
                    <Stack direction="row" justifyContent="center" spacing={0.5}>
                      {equipe.nb_joueurs !== undefined && equipe.nb_joueurs !== null && equipe.nb_joueurs > 0 && (
                        <Chip
                          label={t('competitionsViewer.playerCount', { count: equipe.nb_joueurs })}
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Équipe virtuelle (catégorie d'âge sélection nationale) : endpoint dédié
                            if (equipe.virtual && equipe.countryKey) {
                              openPlayersDialogWithFetch(equipe.nom, async () => {
                                const res = await api.get('/api/comp/national-selections/joueurs', {
                                  params: { countryKey: equipe.countryKey, age: equipe.ageCategory || '' }
                                });
                                return res.data?.joueurs || [];
                              });
                            } else {
                              openPassportDialog({
                                equipeId: equipe.id,
                                scopeName: equipe.nom,
                              });
                            }
                          }}
                          sx={{
                            fontSize: '0.6rem',
                            height: 16,
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            '&:hover': { filter: 'brightness(0.92)' }
                          }}
                        />
                      )}
                    </Stack>
                    
                    {/* Deuxième ligne: répartition des passeports */}
                    {equipe.passport_distribution && Object.keys(equipe.passport_distribution).length > 0 && (
                      <Stack direction="row" justifyContent="center" spacing={0.2} sx={{ flexWrap: 'wrap', gap: 0.2 }}>
                        {Object.entries(equipe.passport_distribution)
                          .filter(([passport, count]) => passport && count > 0)
                          .sort(([a], [b]) => compareLicencias(a, b))
                          .map(([passport, count], index) => {
                            const passportColors = ['#ffecb3', '#e1f5fe', '#f3e5f5', '#e8f5e9', '#fce4ec'];
                            return (
                              <Chip
                                key={passport}
                                icon={<LanguageIcon sx={{ fontSize: '0.55rem !important' }} />}
                                label={`${count} ${passport}`}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPassportDialog({
                                    passport,
                                    equipeId: equipe.id,
                                    scopeName: equipe.nom,
                                  });
                                }}
                                sx={{
                                  fontSize: '0.55rem',
                                  height: 14,
                                  cursor: 'pointer',
                                  backgroundColor: passportColors[index % passportColors.length],
                                  '&:hover': { filter: 'brightness(0.92)' },
                                  '& .MuiChip-icon': {
                                    fontSize: '0.55rem',
                                    ml: 0.3
                                  }
                                }}
                              />
                            );
                          })}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
      </Box>
    </Slide>
    );
  };
  
  // Scroll automatique des logs d'import
  useEffect(() => {
    if (importLogsEndRef.current) {
      importLogsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [importLogs]);

  useEffect(() => {
    if (rosterLogsEndRef.current) {
      rosterLogsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [rosterImportLogs]);

  // Ouvrir le dialog d'import
  const handleOpenImportDialog = () => {
    setImportFormData('');
    setImportLogs([]);
    setImportPlayersData([]);
    setImportError(null);
    setImportSuccess(false);
    setImportTab(0);
    // Reset team import states
    setTeamSearchTerm(selectedEquipe?.nom || '');
    setTeamSearchResults([]);
    setRosterPlayers([]);
    setRosterTeamName('');
    setSelectedRosterPlayers(new Set());
    setRosterImportLogs([]);
    setRosterImportResults([]);
    setRosterImportSuccess(false);
    setRosterImportError(null);
    setOpenImportDialog(true);
  };

  // Fermer le dialog d'import
  const handleCloseImportDialog = () => {
    setOpenImportDialog(false);
    // Si des joueurs ont été importés, rafraîchir la liste
    if ((importSuccess || rosterImportSuccess) && selectedEquipe) {
      loadJoueursByEquipe(selectedEquipe.id);
    }
  };

  // Lancer l'import SSE depuis le dialog
  const handleImportFromDialog = () => {
    setImportLoading(true);
    setImportError(null);
    setImportSuccess(false);
    setImportPlayersData([]);
    setImportLogs([]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const section = process.env.REACT_APP_SECTION;
    const clubId = localStorage.getItem('clubId');
    const lang = localStorage.getItem('language') || 'es';

    const url = `${backendUrl}/api/importar/proballers?details=${encodeURIComponent(
      importFormData
    )}&section=${encodeURIComponent(section)}${clubId ? `&club_id=${clubId}` : ''}&lang=${lang}&equipe_id=${selectedEquipe.id}`;

    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          setImportLogs((prev) => [...prev, data.message]);
        } else if (data.type === 'done') {
          const playersWithEurobasketChoice = data.players.filter(p => p.multipleEurobasket);
          if (playersWithEurobasketChoice.length > 0) {
            const normalPlayers = data.players.filter(p => !p.multipleEurobasket);
            setImportPlayersData(normalPlayers);

            const [firstEurobasket, ...rest] = playersWithEurobasketChoice;
            setImportEurobasketQueue(rest);
            setImportCurrentPlayerName(firstEurobasket.nom);
            setImportEurobasketOptions(firstEurobasket.eurobasketOptions);
            setImportProballersPlayersData(firstEurobasket.proballersPlayersData || []);
            setImportPendingPlayerData({ nom: firstEurobasket.nom });
            setImportEurobasketDialog(true);
          } else {
            setImportPlayersData(data.players);
            setImportSuccess(true);
          }
          setImportLoading(false);
          eventSource.close();
        } else if (data.type === 'error') {
          setImportError(data.message);
          setImportLoading(false);
          eventSource.close();
        }
      } catch (parseError) {
        console.error('Error parsing SSE:', parseError);
      }
    };

    eventSource.onerror = () => {
      setImportError(t('import.connectionError'));
      setImportLoading(false);
      eventSource.close();
    };
  };

  // Gestion du choix Eurobasket dans le dialog d'import
  const handleImportEurobasketOptionClick = async (option, index) => {
    try {
      setImportSelectedOptionIndex(index);
      setImportError(null);

      setImportLogs((prev) => [...prev, `${t('import.selectedEurobasket')}: ${option.name}`]);

      const lang = localStorage.getItem('language') || 'es';
      const resp = await api.post('/api/importar/proballers/save-with-eurobasket', {
        playerData: importPendingPlayerData,
        eurobasketUrl: option.href || option.link,
        eurobasketBirthDate: option.birthDate,
        proballersPlayersData: importProballersPlayersData,
        lang: lang,
        equipe_id: selectedEquipe.id
      });

      if (resp.data.success) {
        setImportLogs((prev) => [...prev, `✓ ${t('import.importedSuccess')}`]);
        setImportPlayersData((prev) => [...prev, {
          nom: importPendingPlayerData.nom,
          found: true,
          alreadyInDB: false,
          multiple: false
        }]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || t('import.importError');
      setImportLogs((prev) => [...prev, `✗ ${t('common.error')}: ${errorMessage}`]);
      setImportError(errorMessage);
    } finally {
      setImportSelectedOptionIndex(null);

      if (importEurobasketQueue.length > 0) {
        const [next, ...rest] = importEurobasketQueue;
        setImportEurobasketQueue(rest);
        setImportCurrentPlayerName(next.nom);
        setImportEurobasketOptions(next.eurobasketOptions);
        setImportProballersPlayersData(next.proballersPlayersData || []);
        setImportPendingPlayerData({ nom: next.nom });
      } else {
        setImportEurobasketDialog(false);
        setImportPendingPlayerData(null);
        setImportEurobasketOptions([]);
        setImportProballersPlayersData([]);
        setImportSuccess(true);
      }
    }
  };

  // Recherche d'équipe Eurobasket
  const handleTeamSearch = async () => {
    if (!teamSearchTerm.trim()) return;
    setTeamSearchLoading(true);
    setTeamSearchResults([]);
    setRosterPlayers([]);
    setRosterTeamName('');
    setSelectedRosterPlayers(new Set());
    setRosterImportResults([]);
    setRosterImportLogs([]);
    setRosterImportSuccess(false);
    setRosterImportError(null);
    try {
      const res = await api.post('/api/importar/eurobasket/search-team', { teamName: teamSearchTerm });
      setTeamSearchResults(res.data.teams || []);
    } catch (err) {
      setRosterImportError(err.response?.data?.message || t('import.connectionError'));
    } finally {
      setTeamSearchLoading(false);
    }
  };

  // Charger le roster d'une équipe Eurobasket
  const handleSelectTeam = async (teamUrl) => {
    setRosterLoading(true);
    setRosterPlayers([]);
    setSelectedRosterPlayers(new Set());
    setRosterImportResults([]);
    setRosterImportLogs([]);
    setRosterImportSuccess(false);
    setRosterImportError(null);
    try {
      const res = await api.post('/api/importar/eurobasket/team-roster', { teamUrl });
      setRosterPlayers(res.data.players || []);
      setRosterTeamName(res.data.teamName || '');
      // Tout sélectionner par défaut
      const allUrls = new Set((res.data.players || []).map(p => p.href));
      setSelectedRosterPlayers(allUrls);
    } catch (err) {
      setRosterImportError(err.response?.data?.message || t('import.connectionError'));
    } finally {
      setRosterLoading(false);
    }
  };

  // Toggle sélection d'un joueur du roster
  const handleToggleRosterPlayer = (href) => {
    setSelectedRosterPlayers(prev => {
      const next = new Set(prev);
      if (next.has(href)) {
        next.delete(href);
      } else {
        next.add(href);
      }
      return next;
    });
  };

  // Tout sélectionner / tout désélectionner
  const handleToggleAllRoster = () => {
    if (selectedRosterPlayers.size === rosterPlayers.length) {
      setSelectedRosterPlayers(new Set());
    } else {
      setSelectedRosterPlayers(new Set(rosterPlayers.map(p => p.href)));
    }
  };

  // Lancer l'import des joueurs sélectionnés du roster
  const handleImportRosterPlayers = () => {
    if (selectedRosterPlayers.size === 0) return;
    setRosterImportLoading(true);
    setRosterImportError(null);
    setRosterImportSuccess(false);
    setRosterImportResults([]);
    setRosterImportLogs([]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const section = process.env.REACT_APP_SECTION;
    const clubId = localStorage.getItem('clubId');
    const lang = localStorage.getItem('language') || 'es';

    const playerUrlsJson = JSON.stringify([...selectedRosterPlayers]);
    const url = `${backendUrl}/api/importar/eurobasket/import-players?playerUrls=${encodeURIComponent(playerUrlsJson)}&section=${encodeURIComponent(section)}${clubId ? `&club_id=${clubId}` : ''}&lang=${lang}&equipe_id=${selectedEquipe.id}`;

    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          setRosterImportLogs((prev) => [...prev, data.message]);
        } else if (data.type === 'done') {
          setRosterImportResults(data.players || []);
          setRosterImportSuccess(true);
          setRosterImportLoading(false);
          eventSource.close();
        } else if (data.type === 'error') {
          setRosterImportError(data.message);
          setRosterImportLoading(false);
          eventSource.close();
        }
      } catch (parseError) {
        console.error('Error parsing SSE:', parseError);
      }
    };

    eventSource.onerror = () => {
      setRosterImportError(t('import.connectionError'));
      setRosterImportLoading(false);
      eventSource.close();
    };
  };

  // Rendu des joueurs
  const renderJoueurs = () => {
    // Initialiser toutes les positions avec des tableaux vides
    const playersByPosition = {};
    positionOrder.forEach(pos => {
      playersByPosition[pos] = [];
    });
    
    // Grouper les joueurs par position normalisée
    joueurs.forEach(joueur => {
      const normalizedPosition = normalizePosition(joueur.poste);
      if (!playersByPosition[normalizedPosition]) {
        playersByPosition[normalizedPosition] = [];
      }
      playersByPosition[normalizedPosition].push({
        ...joueur,
        normalizedPosition
      });
    });
    
    // Trier les joueurs dans chaque position par nom
    Object.keys(playersByPosition).forEach(position => {
      if (playersByPosition[position].length > 0) {
        playersByPosition[position].sort((a, b) => a.nom.localeCompare(b.nom));
      }
    });
    
    // Toujours utiliser l'ordre complet des positions
    const orderedPositions = [...positionOrder];
    
    // Ajouter les positions non définies dans l'ordre (si existantes)
    Object.keys(playersByPosition).forEach(pos => {
      if (!positionOrder.includes(pos) && playersByPosition[pos].length > 0) {
        orderedPositions.push(pos);
      }
    });
    
    return (
      <Slide direction={slideDirection} in={currentView === 'joueurs'} mountOnEnter unmountOnExit>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'bottom', color: '#F25F29' }} />
            {t('competitionsViewer.players')} - {selectedEquipe?.nom} ({joueurs.length})
          </Typography>

          {joueurs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                {t('competitionsViewer.noPlayersFound')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {orderedPositions.map((position) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={position}>
                  <Box>
                    {/* Position header */}
                    <Typography
                      variant="h6"
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        mb: 1.5,
                        p: 0.8,
                        backgroundColor: positionColors[position] || '#757575',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        borderRadius: 1,
                        boxShadow: 2
                      }}
                    >
                      {position === NO_POSITION_KEY ? t('competitionsViewer.positions.noPosition') : translateFromDB(position)} ({playersByPosition[position].length})
                    </Typography>
                    
                    {/* Column of players for this position */}
                    <Stack spacing={1} sx={{ minHeight: playersByPosition[position].length === 0 ? '100px' : 'auto' }}>
                      {playersByPosition[position].length === 0 ? (
                        <Box sx={{
                          p: 2,
                          textAlign: 'center',
                          border: '2px dashed',
                          borderColor: '#e0e0e0',
                          borderRadius: 1,
                          backgroundColor: '#fafafa'
                        }}>
                          <Typography variant="caption" color="text.disabled">
                            {t('competitionsViewer.noPlayers')}
                          </Typography>
                        </Box>
                      ) : (
                        playersByPosition[position].map((joueur) => (
                        <Card
                          key={joueur.id}
                          id={`player-${joueur.id}`}
                          sx={{
                            p: 1.5,
                            position: 'relative',
                            overflow: 'visible',
                            transition: 'all 0.3s',
                            border: '2px solid',
                            borderColor: positionColors[position] || '#757575',
                            '&:hover': {
                              boxShadow: 4,
                              backgroundColor: '#f5f5f5',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          {/* Indicateur de blessure en haut à droite de la carte */}
                          {joueur.in_injury === 1 && (
                            <Tooltip title={t('competitionsViewer.badges.injury')}>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  backgroundColor: theme.palette.error.main,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 22,
                                  height: 22,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                  border: '2px solid white',
                                  zIndex: 1
                                }}
                              >
                                <HealingIcon sx={{ fontSize: 14, color: 'white' }} />
                              </Box>
                            </Tooltip>
                          )}
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ position: 'relative' }}>
                              <Avatar
                                src={getPlayerAvatarUrl(joueur.id)}
                                sx={{ width: 50, height: 50 }}
                              >
                                {joueur.nom?.charAt(0)}
                              </Avatar>
                              {/* Indicateur de forme en bas à droite */}
                              {joueur.forme_indicator && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: -2,
                                    right: -2,
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 22,
                                    height: 22,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                  }}
                                >
                                  {getFormIcon(joueur.forme_indicator)}
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography 
                                variant="body2" 
                                component="a"
                                href={`/players/${joueur.id}`}
                                target="_blank"
                                sx={{
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  color: 'primary.main',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.3,
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {joueur.nom}
                              </Typography>
                              
                              {/* Ligne 2: Année (Age) - Taille */}
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', mt: 0.3 }}>
                                {[
                                  joueur.annee_naissance ? 
                                    `${joueur.annee_naissance}${joueur.date_naissance ? ` (${calculateAge(joueur.date_naissance)})` : ''}` 
                                    : null,
                                  joueur.taille ? `${joueur.taille}cm` : null
                                ].filter(Boolean).join(' - ')}
                              </Typography>
                              
                              {/* Ligne 3: Nationalité - Passeport */}
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                {joueur.nationalite ? t(`createPlayer.nationalities.${joueur.nationalite}`, joueur.nationalite) : ''}
                                {joueur.passport && (
                                  <>
                                    {' - '}
                                    <Chip
                                      icon={<LanguageIcon sx={{ fontSize: '0.7rem' }} />}
                                      label={joueur.passport}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        ml: 0.5,
                                        '& .MuiChip-icon': {
                                          fontSize: '0.75rem',
                                          ml: 0.5
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              </Typography>
                            </Box>
                          </Stack>
                          
                          {/* Badges */}
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <PlayerBadges player={joueur} theme={theme} t={t} />
                            {joueur.is_cedido === 1 && (
                              <Chip
                                label={`${t('competitionsViewer.cedidos.loanedFrom')} ${joueur.equipe_proprietaire_nom}`}
                                size="small"
                                onClick={() => handleSelectEquipe({ id: joueur.equipe_proprietaire_id, nom: joueur.equipe_proprietaire_nom })}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  backgroundColor: '#ff9800',
                                  color: 'white',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#e68a00'
                                  }
                                }}
                              />
                            )}
                            {joueur.is_ncaa_committed === 1 && (
                              <Chip
                                label={`${t('competitionsViewer.ncaaCommits.committedTo')} ${joueur.ncaa_commitment_nom}`}
                                size="small"
                                onClick={() => handleSelectEquipe({ id: joueur.ncaa_commitment_id, nom: joueur.ncaa_commitment_nom })}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  backgroundColor: '#1976d2',
                                  color: 'white',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#115293'
                                  }
                                }}
                              />
                            )}
                            {joueur.is_tanteo === 1 && joueur.tanteo_nom && (
                              <Chip
                                icon={<GavelIcon sx={{ fontSize: '0.7rem !important', color: 'white !important' }} />}
                                label={`Tanteo: ${joueur.tanteo_nom}`}
                                size="small"
                                onClick={() => handleSelectEquipe({ id: joueur.tanteo_id, nom: joueur.tanteo_nom })}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  backgroundColor: '#f9a825',
                                  color: 'white',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#f57f17'
                                  }
                                }}
                              />
                            )}
                            {joueur.is_free_agent === 1 && (
                              <Chip
                                label={t('createPlayer.fields.freeAgentBadge')}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  backgroundColor: '#4caf50',
                                  color: 'white'
                                }}
                              />
                            )}
                            {/* Sur la liste virtuelle Free Agents : afficher l'équipe actuelle si présente */}
                            {isFreeAgentVirtualId(selectedEquipe?.id) && joueur.equipe_actuelle_id && (
                              <Chip
                                label={`${t('competitionsViewer.freeAgents.currentlyAt')} ${joueur.equipe_actuelle_nom}`}
                                size="small"
                                onClick={() => handleSelectEquipe({ id: joueur.equipe_actuelle_id, nom: joueur.equipe_actuelle_nom })}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  backgroundColor: '#757575',
                                  color: 'white',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#5a5a5a'
                                  }
                                }}
                              />
                            )}
                          </Box>
                        </Card>
                      ))
                      )}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Section Jugadores Cedidos (sortants) */}
          {cedidosSortants.length > 0 && (() => {
            // Grouper les cedidos par position
            const cedidosByPosition = {};
            positionOrder.forEach(pos => {
              cedidosByPosition[pos] = [];
            });
            cedidosSortants.forEach(joueur => {
              const normalizedPosition = normalizePosition(joueur.poste);
              if (!cedidosByPosition[normalizedPosition]) {
                cedidosByPosition[normalizedPosition] = [];
              }
              cedidosByPosition[normalizedPosition].push(joueur);
            });
            // Filtrer les positions qui ont des joueurs
            const cedidoPositions = [...positionOrder];
            Object.keys(cedidosByPosition).forEach(pos => {
              if (!positionOrder.includes(pos) && cedidosByPosition[pos].length > 0) {
                cedidoPositions.push(pos);
              }
            });
            const activeCedidoPositions = cedidoPositions.filter(pos => cedidosByPosition[pos]?.length > 0);

            return (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: '#4caf50' }}>
                  {t('competitionsViewer.cedidos.title')} ({cedidosSortants.length})
                </Typography>
                <Grid container spacing={2}>
                  {activeCedidoPositions.map((position) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={`cedido-${position}`}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            mb: 1.5,
                            p: 0.8,
                            backgroundColor: positionColors[position] || '#757575',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            borderRadius: 1,
                            boxShadow: 2
                          }}
                        >
                          {position === NO_POSITION_KEY ? t('competitionsViewer.positions.noPosition') : translateFromDB(position)} ({cedidosByPosition[position].length})
                        </Typography>
                        <Stack spacing={1}>
                          {cedidosByPosition[position].map((joueur) => (
                            <Card
                              key={joueur.id}
                              id={`player-${joueur.id}`}
                              sx={{
                                p: 1.5,
                                position: 'relative',
                                overflow: 'visible',
                                transition: 'all 0.3s',
                                border: '2px solid',
                                borderColor: positionColors[position] || '#757575',
                                '&:hover': {
                                  boxShadow: 4,
                                  backgroundColor: '#f5f5f5',
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            >
                              {/* Indicateur de blessure */}
                              {joueur.in_injury === 1 && (
                                <Tooltip title={t('competitionsViewer.badges.injury')}>
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      backgroundColor: theme.palette.error.main,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 22,
                                      height: 22,
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                      border: '2px solid white',
                                      zIndex: 1
                                    }}
                                  >
                                    <HealingIcon sx={{ fontSize: 14, color: 'white' }} />
                                  </Box>
                                </Tooltip>
                              )}
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{ position: 'relative' }}>
                                  <Avatar
                                    src={getPlayerAvatarUrl(joueur.id)}
                                    sx={{ width: 50, height: 50 }}
                                  >
                                    {joueur.nom?.charAt(0)}
                                  </Avatar>
                                  {joueur.forme_indicator && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -2,
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 22,
                                        height: 22,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      {getFormIcon(joueur.forme_indicator)}
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    component="a"
                                    href={`/players/${joueur.id}`}
                                    target="_blank"
                                    sx={{
                                      fontWeight: 'bold',
                                      textDecoration: 'none',
                                      color: 'primary.main',
                                      fontSize: '0.95rem',
                                      lineHeight: 1.3,
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                  >
                                    {joueur.nom}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', mt: 0.3 }}>
                                    {[
                                      joueur.annee_naissance ?
                                        `${joueur.annee_naissance}${joueur.date_naissance ? ` (${calculateAge(joueur.date_naissance)})` : ''}`
                                        : null,
                                      joueur.taille ? `${joueur.taille}cm` : null
                                    ].filter(Boolean).join(' - ')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                    {joueur.nationalite ? t(`createPlayer.nationalities.${joueur.nationalite}`, joueur.nationalite) : ''}
                                    {joueur.passport && (
                                      <>
                                        {' - '}
                                        <Chip
                                          icon={<LanguageIcon sx={{ fontSize: '0.7rem' }} />}
                                          label={joueur.passport}
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: '0.65rem',
                                            ml: 0.5,
                                            '& .MuiChip-icon': {
                                              fontSize: '0.75rem',
                                              ml: 0.5
                                            }
                                          }}
                                        />
                                      </>
                                    )}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <PlayerBadges player={joueur} theme={theme} t={t} />
                                <Chip
                                  label={`${t('competitionsViewer.cedidos.loanedTo')} ${joueur.equipe_actuelle_nom}`}
                                  size="small"
                                  onClick={() => handleSelectEquipe({ id: joueur.equipe_actuelle_id, nom: joueur.equipe_actuelle_nom })}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 'bold',
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: '#e68a00'
                                    }
                                  }}
                                />
                              </Box>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })()}

          {/* Section Recrues NCAA (commits entrants) — visible uniquement sur une équipe NCAA */}
          {(/NCAA/i.test(selectedNiveau?.nom || '') || /NCAA/i.test(selectedCompetition?.nom || '')) && ncaaCommitsEntrants.length > 0 && (() => {
            const commitsByPosition = {};
            positionOrder.forEach(pos => {
              commitsByPosition[pos] = [];
            });
            ncaaCommitsEntrants.forEach(joueur => {
              const normalizedPosition = normalizePosition(joueur.poste);
              if (!commitsByPosition[normalizedPosition]) {
                commitsByPosition[normalizedPosition] = [];
              }
              commitsByPosition[normalizedPosition].push(joueur);
            });
            const commitPositions = [...positionOrder];
            Object.keys(commitsByPosition).forEach(pos => {
              if (!positionOrder.includes(pos) && commitsByPosition[pos].length > 0) {
                commitPositions.push(pos);
              }
            });
            const activeCommitPositions = commitPositions.filter(pos => commitsByPosition[pos]?.length > 0);

            return (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                  {t('competitionsViewer.ncaaCommits.title')} ({ncaaCommitsEntrants.length})
                </Typography>
                <Grid container spacing={2}>
                  {activeCommitPositions.map((position) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={`commit-${position}`}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            mb: 1.5,
                            p: 0.8,
                            backgroundColor: positionColors[position] || '#757575',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            borderRadius: 1,
                            boxShadow: 2
                          }}
                        >
                          {position === NO_POSITION_KEY ? t('competitionsViewer.positions.noPosition') : translateFromDB(position)} ({commitsByPosition[position].length})
                        </Typography>
                        <Stack spacing={1}>
                          {commitsByPosition[position].map((joueur) => (
                            <Card
                              key={joueur.id}
                              id={`player-${joueur.id}`}
                              sx={{
                                p: 1.5,
                                position: 'relative',
                                overflow: 'visible',
                                transition: 'all 0.3s',
                                border: '2px solid',
                                borderColor: positionColors[position] || '#757575',
                                '&:hover': {
                                  boxShadow: 4,
                                  backgroundColor: '#f5f5f5',
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            >
                              {joueur.in_injury === 1 && (
                                <Tooltip title={t('competitionsViewer.badges.injury')}>
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      backgroundColor: theme.palette.error.main,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 22,
                                      height: 22,
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                      border: '2px solid white',
                                      zIndex: 1
                                    }}
                                  >
                                    <HealingIcon sx={{ fontSize: 14, color: 'white' }} />
                                  </Box>
                                </Tooltip>
                              )}
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{ position: 'relative' }}>
                                  <Avatar
                                    src={getPlayerAvatarUrl(joueur.id)}
                                    sx={{ width: 50, height: 50 }}
                                  >
                                    {joueur.nom?.charAt(0)}
                                  </Avatar>
                                  {joueur.forme_indicator && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -2,
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 22,
                                        height: 22,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      {getFormIcon(joueur.forme_indicator)}
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    component="a"
                                    href={`/players/${joueur.id}`}
                                    target="_blank"
                                    sx={{
                                      fontWeight: 'bold',
                                      textDecoration: 'none',
                                      color: 'primary.main',
                                      fontSize: '0.95rem',
                                      lineHeight: 1.3,
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                  >
                                    {joueur.nom}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', mt: 0.3 }}>
                                    {[
                                      joueur.annee_naissance ?
                                        `${joueur.annee_naissance}${joueur.date_naissance ? ` (${calculateAge(joueur.date_naissance)})` : ''}`
                                        : null,
                                      joueur.taille ? `${joueur.taille}cm` : null
                                    ].filter(Boolean).join(' - ')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                    {joueur.nationalite ? t(`createPlayer.nationalities.${joueur.nationalite}`, joueur.nationalite) : ''}
                                    {joueur.passport && (
                                      <>
                                        {' - '}
                                        <Chip
                                          icon={<LanguageIcon sx={{ fontSize: '0.7rem' }} />}
                                          label={joueur.passport}
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: '0.65rem',
                                            ml: 0.5,
                                            '& .MuiChip-icon': {
                                              fontSize: '0.75rem',
                                              ml: 0.5
                                            }
                                          }}
                                        />
                                      </>
                                    )}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <PlayerBadges player={joueur} theme={theme} t={t} />
                                <Chip
                                  label={`${t('competitionsViewer.ncaaCommits.incomingFrom')} ${joueur.equipe_actuelle_nom}`}
                                  size="small"
                                  onClick={() => handleSelectEquipe({ id: joueur.equipe_actuelle_id, nom: joueur.equipe_actuelle_nom })}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 'bold',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: '#115293'
                                    }
                                  }}
                                />
                              </Box>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })()}
        </Box>
      </Slide>
    );
  };

  return (
    <>
      <LoadingBackdrop open={loading} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
            '& .Mui-selected': { color: '#F25F29' },
            '& .MuiTabs-indicator': { backgroundColor: '#F25F29' },
          }}
        >
          <Tab icon={<TrophyIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('layout.menu.competitions')} />
          <Tab icon={<CompareArrowsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('competitionPerformance.title')} />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box sx={{ py: 2, px: { xs: 0.5, sm: 1, md: 0 } }}>
          {renderBreadcrumb()}
          <Box sx={{ minHeight: '60vh' }}>
            {currentView === 'niveaux' && renderNiveaux()}
            {currentView === 'competitions' && renderCompetitions()}
            {currentView === 'equipes' && renderEquipes()}
            {currentView === 'joueurs' && renderJoueurs()}
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <CompetitionPerformance />
      )}

      {/* FAB flottant pour importer des joueurs */}
      {currentView === 'joueurs' && selectedEquipe && !isVirtualId(selectedEquipe.id) && (
        <Fab
          color="primary"
          onClick={handleOpenImportDialog}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            background: 'linear-gradient(135deg, #F25F29 0%, #ff8a50 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d94e1f 0%, #e67640 100%)',
            },
            zIndex: 1000
          }}
        >
          <Tooltip title={t('competitions.import.importPlayers')}>
            <AddIcon />
          </Tooltip>
        </Fab>
      )}

      {/* Dialog d'import de joueurs */}
      <Dialog
        open={openImportDialog}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #F25F29 0%, #ff8a50 100%)', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            {t('competitions.import.importPlayers')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {selectedEquipe?.nom}
          </Typography>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={importTab} onChange={(_, v) => setImportTab(v)} sx={{ '& .MuiTab-root': { fontWeight: 'bold' } }}>
            <Tab label={t('competitions.import.byName')} />
            <Tab label={t('competitions.import.byTeam')} />
          </Tabs>
        </Box>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* ===== ONGLET 0 : Import par nom ===== */}
          {importTab === 0 && (
            <>
              {importError && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {importError}
                </Alert>
              )}
              {importSuccess && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {t('import.importSuccess')}
                </Alert>
              )}

              <TextField
                fullWidth
                label={t('import.playerNames')}
                value={importFormData}
                onChange={(e) => setImportFormData(e.target.value)}
                multiline
                rows={4}
                variant="outlined"
                placeholder={t('import.placeholder')}
                disabled={importLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#F25F29' },
                    '&.Mui-focused fieldset': { borderColor: '#F25F29' },
                  }
                }}
              />

              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={importLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                disabled={importLoading || !importFormData.trim()}
                onClick={handleImportFromDialog}
                sx={{
                  py: 1.2,
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #F25F29 0%, #ff8a50 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #d94e1f 0%, #e67640 100%)' },
                  '&:disabled': { background: '#cccccc' }
                }}
              >
                {importLoading ? t('import.searching') : t('import.searchAndImport')}
              </Button>

              {importLogs.length > 0 && (
                <>
                  <Divider />
                  <Paper elevation={0} sx={{ p: 2, maxHeight: 200, overflow: 'auto', backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                      {t('import.activityLog')}
                    </Typography>
                    {importLogs.map((line, i) => (
                      <Typography variant="body2" key={i} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {line}
                      </Typography>
                    ))}
                    <div ref={importLogsEndRef} />
                  </Paper>
                </>
              )}

              {importPlayersData.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {t('import.importResults')}
                  </Typography>
                  {importPlayersData.map((player, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: player.alreadyInDB ? '#fff3e0' : player.found === false ? '#ffebee' : player.multipleEurobasket ? '#fff3e0' : '#e8f5e9'
                      }}
                    >
                      {player.multipleEurobasket ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={t('import.multipleEurobasketOptions')} sx={{ backgroundColor: '#ff6b35', color: 'white' }} size="small" />
                          <Typography variant="body2" fontWeight="bold">{player.nom}</Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setImportCurrentPlayerName(player.nom);
                              setImportEurobasketOptions(player.eurobasketOptions);
                              setImportProballersPlayersData(player.proballersPlayersData || []);
                              setImportPendingPlayerData({ nom: player.nom });
                              setImportEurobasketDialog(true);
                            }}
                            sx={{ ml: 'auto', background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)' }}
                          >
                            {t('import.viewOptions')} ({player.eurobasketOptions?.length || 0})
                          </Button>
                        </Box>
                      ) : player.found === false ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={t('import.notFound')} color="error" size="small" />
                          <Typography variant="body2" fontWeight="bold">{player.nom}</Typography>
                        </Box>
                      ) : player.alreadyInDB ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={t('import.alreadyExists')} color="warning" size="small" />
                          <Typography variant="body2" fontWeight="bold">{player.nom}</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={`✓ ${t('import.imported')}`} color="success" size="small" />
                          <Typography variant="body2" fontWeight="bold">{player.nom}</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </>
          )}

          {/* ===== ONGLET 1 : Import par équipe Eurobasket ===== */}
          {importTab === 1 && (
            <>
              {rosterImportError && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {rosterImportError}
                </Alert>
              )}
              {rosterImportSuccess && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {t('import.importSuccess')}
                </Alert>
              )}

              {/* Étape 1: Recherche d'équipe */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={t('competitions.import.searchTeam')}
                  value={teamSearchTerm}
                  onChange={(e) => setTeamSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTeamSearch()}
                  disabled={teamSearchLoading || rosterLoading || rosterImportLoading}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#F25F29' },
                      '&.Mui-focused fieldset': { borderColor: '#F25F29' },
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleTeamSearch}
                  disabled={teamSearchLoading || !teamSearchTerm.trim() || rosterImportLoading}
                  startIcon={teamSearchLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                  sx={{
                    minWidth: 120,
                    background: 'linear-gradient(135deg, #F25F29 0%, #ff8a50 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #d94e1f 0%, #e67640 100%)' },
                    '&:disabled': { background: '#cccccc' }
                  }}
                >
                  {t('common.search') || 'Buscar'}
                </Button>
              </Box>

              {/* Résultats de recherche d'équipe */}
              {teamSearchResults.length > 0 && rosterPlayers.length === 0 && (
                <Paper elevation={0} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, maxHeight: 250, overflow: 'auto' }}>
                  <List dense>
                    {teamSearchResults.map((team, idx) => (
                      <ListItem key={idx} disablePadding>
                        <ListItemButton onClick={() => handleSelectTeam(team.href)} disabled={rosterLoading}>
                          <ListItemText
                            primary={<Typography variant="body2" fontWeight="bold">{team.name}</Typography>}
                            secondary={`${team.country} - ${team.league}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Loading roster */}
              {rosterLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress sx={{ color: '#F25F29' }} />
                </Box>
              )}

              {/* Étape 2: Roster avec checkboxes */}
              {rosterPlayers.length > 0 && !rosterImportLoading && rosterImportResults.length === 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {rosterTeamName} — {rosterPlayers.length} {t('competitionsViewer.players')}
                    </Typography>
                    <Button size="small" onClick={handleToggleAllRoster}>
                      {selectedRosterPlayers.size === rosterPlayers.length
                        ? t('competitions.import.deselectAll')
                        : t('competitions.import.selectAll')}
                    </Button>
                  </Box>

                  <Paper elevation={0} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {rosterPlayers.map((player, idx) => (
                        <ListItem
                          key={idx}
                          dense
                          sx={{
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: selectedRosterPlayers.has(player.href) ? '#e8f5e9' : 'transparent',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: selectedRosterPlayers.has(player.href) ? '#c8e6c9' : '#eeeeee' }
                          }}
                          onClick={() => handleToggleRosterPlayer(player.href)}
                        >
                          <Box sx={{ mr: 1 }}>
                            <input
                              type="checkbox"
                              checked={selectedRosterPlayers.has(player.href)}
                              onChange={() => {}}
                              style={{ cursor: 'pointer' }}
                            />
                          </Box>
                          <ListItemText
                            primary={<Typography variant="body2" fontWeight="bold">{player.name}</Typography>}
                            secondary={[
                              player.height ? `${player.height}cm` : null,
                              player.position || null,
                              player.nationality || null
                            ].filter(Boolean).join(' - ')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<AddIcon />}
                    disabled={selectedRosterPlayers.size === 0}
                    onClick={handleImportRosterPlayers}
                    sx={{
                      py: 1.2,
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #F25F29 0%, #ff8a50 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #d94e1f 0%, #e67640 100%)' },
                      '&:disabled': { background: '#cccccc' }
                    }}
                  >
                    {t('competitions.import.importSelected')} ({selectedRosterPlayers.size})
                  </Button>
                </>
              )}

              {/* Logs d'import du roster */}
              {rosterImportLogs.length > 0 && (
                <>
                  <Divider />
                  <Paper elevation={0} sx={{ p: 2, maxHeight: 200, overflow: 'auto', backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                      {t('import.activityLog')}
                    </Typography>
                    {rosterImportLogs.map((line, i) => (
                      <Typography variant="body2" key={i} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {line}
                      </Typography>
                    ))}
                    <div ref={rosterLogsEndRef} />
                  </Paper>
                </>
              )}

              {/* Résultats d'import du roster */}
              {rosterImportResults.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {t('import.importResults')}
                  </Typography>
                  {rosterImportResults.map((player, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: player.alreadyInDB ? '#fff3e0' : player.found === false ? '#ffebee' : '#e8f5e9'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {player.found === false ? (
                          <Chip label={t('import.notFound')} color="error" size="small" />
                        ) : player.alreadyInDB ? (
                          <Chip label={t('import.alreadyExists')} color="warning" size="small" />
                        ) : (
                          <Chip label={`✓ ${t('import.imported')}`} color="success" size="small" />
                        )}
                        <Typography variant="body2" fontWeight="bold">{player.nom}</Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseImportDialog} variant="outlined">
            {(importSuccess || rosterImportSuccess) ? t('common.close') : t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog "joueurs par passeport" — clic sur les chips de répartition */}
      <Dialog
        open={passportDialogOpen}
        onClose={closePassportDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #F25F29 0%, #ff8a50 100%)', color: 'white' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LanguageIcon />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {passportDialogTitle}
              </Typography>
              {!passportDialogLoading && (
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t('competitionsViewer.playerCount', { count: passportDialogPlayers.length })}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {passportDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#F25F29' }} />
            </Box>
          ) : passportDialogPlayers.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              {t('competitionsViewer.noPlayersFound')}
            </Typography>
          ) : (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {passportDialogPlayers.map((joueur) => {
                const normalizedPosition = normalizePosition(joueur.poste);
                const borderColor = positionColors[normalizedPosition] || '#757575';
                return (
                  <Grid item xs={12} sm={6} md={4} key={joueur.id}>
                    <Card
                      sx={{
                        p: 1.5,
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.2s',
                        border: '2px solid',
                        borderColor,
                        '&:hover': { boxShadow: 4, backgroundColor: '#f5f5f5' }
                      }}
                    >
                      {joueur.in_injury === 1 && (
                        <Tooltip title={t('competitionsViewer.badges.injury')}>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              backgroundColor: theme.palette.error.main,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 22,
                              height: 22,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                              border: '2px solid white',
                              zIndex: 1
                            }}
                          >
                            <HealingIcon sx={{ fontSize: 14, color: 'white' }} />
                          </Box>
                        </Tooltip>
                      )}
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ position: 'relative' }}>
                          <Avatar src={getPlayerAvatarUrl(joueur.id)} sx={{ width: 50, height: 50 }}>
                            {joueur.nom?.charAt(0)}
                          </Avatar>
                          {joueur.forme_indicator && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -2,
                                right: -2,
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 22,
                                height: 22,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}
                            >
                              {getFormIcon(joueur.forme_indicator)}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={`/players/${joueur.id}`}
                            target="_blank"
                            sx={{
                              fontWeight: 'bold',
                              textDecoration: 'none',
                              color: 'primary.main',
                              fontSize: '0.95rem',
                              lineHeight: 1.3,
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {joueur.nom}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', mt: 0.3 }}>
                            {[
                              joueur.annee_naissance
                                ? `${joueur.annee_naissance}${joueur.date_naissance ? ` (${calculateAge(joueur.date_naissance)})` : ''}`
                                : null,
                              joueur.taille ? `${joueur.taille}cm` : null,
                              joueur.poste ? translateFromDB(normalizedPosition) : null
                            ].filter(Boolean).join(' - ')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                            {joueur.nationalite ? t(`createPlayer.nationalities.${joueur.nationalite}`, joueur.nationalite) : ''}
                            {joueur.passport && (
                              <>
                                {' - '}
                                <Chip
                                  icon={<LanguageIcon sx={{ fontSize: '0.7rem' }} />}
                                  label={joueur.passport}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    ml: 0.5,
                                    '& .MuiChip-icon': { fontSize: '0.75rem', ml: 0.5 }
                                  }}
                                />
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <PlayerBadges player={joueur} theme={theme} t={t} />
                        {joueur.is_tanteo === 1 && joueur.tanteo_nom && (
                          <Chip
                            icon={
                              <Box
                                component="img"
                                src={getLogoUrl(joueur.tanteo_id, 'equipes')}
                                alt={joueur.tanteo_nom}
                                sx={{ width: 14, height: 14, objectFit: 'contain', borderRadius: '2px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            }
                            label={`Tanteo: ${joueur.tanteo_nom}`}
                            size="small"
                            onClick={() => {
                              closePassportDialog();
                              fastNavigateToEquipe(joueur.tanteo_id);
                            }}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              backgroundColor: '#f9a825',
                              color: 'white',
                              cursor: 'pointer',
                              '& .MuiChip-icon': { ml: 0.5 },
                              '&:hover': { backgroundColor: '#e69500' }
                            }}
                          />
                        )}
                        {joueur.is_cedido === 1 && joueur.equipe_proprietaire_nom && (
                          <Chip
                            icon={
                              <Box
                                component="img"
                                src={getLogoUrl(joueur.equipe_proprietaire_id, 'equipes')}
                                alt={joueur.equipe_proprietaire_nom}
                                sx={{ width: 14, height: 14, objectFit: 'contain', borderRadius: '2px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            }
                            label={`${t('competitionsViewer.cedidos.loanedFrom')} ${joueur.equipe_proprietaire_nom}`}
                            size="small"
                            onClick={() => {
                              closePassportDialog();
                              fastNavigateToEquipe(joueur.equipe_proprietaire_id);
                            }}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              backgroundColor: '#ff9800',
                              color: 'white',
                              cursor: 'pointer',
                              '& .MuiChip-icon': { ml: 0.5 },
                              '&:hover': { backgroundColor: '#e68a00' }
                            }}
                          />
                        )}
                        {joueur.equipe_actuelle_id && joueur.equipe_actuelle_nom && (
                          <Chip
                            icon={
                              <Box
                                component="img"
                                src={getLogoUrl(joueur.equipe_actuelle_id, 'equipes')}
                                alt={joueur.equipe_actuelle_nom}
                                sx={{ width: 14, height: 14, objectFit: 'contain', borderRadius: '2px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            }
                            label={joueur.equipe_actuelle_nom}
                            size="small"
                            onClick={() => {
                              closePassportDialog();
                              fastNavigateToEquipe(joueur.equipe_actuelle_id);
                            }}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              backgroundColor: '#757575',
                              color: 'white',
                              cursor: 'pointer',
                              '& .MuiChip-icon': { ml: 0.5 },
                              '&:hover': { backgroundColor: '#5a5a5a' }
                            }}
                          />
                        )}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closePassportDialog} variant="outlined">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Eurobasket (choix multiples) pour l'import */}
      <Dialog
        open={importEurobasketDialog}
        onClose={() => {
          setImportEurobasketDialog(false);
          setImportEurobasketQueue([]);
          setImportPendingPlayerData(null);
          setImportEurobasketOptions([]);
          setImportProballersPlayersData([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            {t('import.selectEurobasketPlayer')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {importCurrentPlayerName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('import.multipleEurobasketPlayersFound')}
          </Alert>
          <List>
            {importEurobasketOptions.map((option, idx) => (
              <ListItem
                key={idx}
                sx={{
                  mb: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: '#fff3e0' }
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {option.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {option.birthDate && <strong>{t('players.birthDate')}: {option.birthDate}</strong>}
                        {option.team && ` • ${t('players.team')}: ${option.team}`}
                        {option.position && ` • ${t('players.position')}: ${option.position}`}
                        {option.height && ` • ${option.height}`}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        {option.href || option.link}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={t('common.viewProfile') || 'Ver ficha'}>
                    <IconButton
                      edge="end"
                      onClick={() => window.open(option.href || option.link, '_blank')}
                      sx={{ color: '#667eea' }}
                      disabled={importSelectedOptionIndex !== null}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.select') || 'Seleccionar'}>
                    <IconButton
                      edge="end"
                      onClick={() => handleImportEurobasketOptionClick(option, idx)}
                      sx={{ color: importSelectedOptionIndex === idx ? '#ff9800' : '#4caf50' }}
                      disabled={importSelectedOptionIndex !== null}
                    >
                      {importSelectedOptionIndex === idx ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <CheckCircleIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setImportEurobasketDialog(false)} variant="outlined">
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Export par défaut du composant
export default CompetitionsViewer;